import React, { memo, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
  DataGridPro,
  useGridApiRef,
  Toolbar,
} from '@mui/x-data-grid-pro';
import {
  GridToolbarColumnsButton,
  GridToolbarExportButton,
  GridToolbarFilterButton,
} from '../utils/gridToolbarParts.js';
import { withSelectAllAwareCheckbox } from '../utils/gridSelectAllCheckbox.js';
import { CustomQuickFilter } from '../utils/CustomQuickFilter.js';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import FlagIcon from '@mui/icons-material/Flag';
import BadDataFlagsSelector from './BadDataFlagsSelector.js';
import RefreshIcon from '@mui/icons-material/Refresh';
import { usePersistedDataStore } from '../../store.js';
import { useUser } from '../../UserContext.js';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import PublicIcon from '@mui/icons-material/Public';
import InfoIcon from '@mui/icons-material/Info';
import { updateDatabaseNewFunction, InventoryUpdateValidationError } from '../../../common/updateDB.js';
import { INVENTORY_UPDATE_VALIDATION_TOAST_OPTIONS } from '../CaseReviewHelpers/inventoryUpdateProtections.js';
import {toast} from '../../../common/toast'
import StableMuiDialog from '../../../common/StableMuiDialog.js';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import { styleOptions, STATE } from '../../../common/mappings.js';
import { isEqual } from 'lodash';
import {
  createEmptyRowSelection,
  getSelectedRowIds,
} from '../utils/gridRowSelectionModel.js';
import CircularProgress from '@mui/material/CircularProgress';
import { fetchBadDataReport } from './apiUtils.js';
import { completeCaseStatus } from '../../../common/CaseStatusUpdater.js';
import { flagMultipleBadSales, prepareSalesData } from '../../../common/flagBadSaleHelper.js';
import FlagBadSaleDialog from '../FlagBadSaleDialog.js';
import { 
  columnHeaderMappings,
  AVAILABLE_BAD_DATA_FIELDS,
  numericEditableFields,
  booleanLikeFields,
  ALWAYS_EDITABLE_FIELDS,
  OUTLIER_FIELDS,
  EDITABLE_FIELDS
} from '../../../common/mappings.js';
import axiosInstance from '../../../fetchConfig/axiosConfig.js';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CopyableIdCell from '../../../common/CopyableIdCell.js';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PhotoCell from '../Photos/PhotoCell.js';
import { DATA_ANALYSIS_DEFAULT_VISIBLE_FIELDS } from './columnDefinitions.js';
import logger from '../../../utils/logger';

// AddressCell component with internal state
const AddressCell = memo(({ row, value, onRequestPropertyInfo, onShowPropertyInfoConfirm, onDataNotAvailable }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = useCallback((event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleAskAI = useCallback(() => {
    const address = `${row.Address}, ${row.Town}, ${STATE} ${row.ZipCode}`;
    logger.log('AI Address String:', address);
    window.open(`https://chat.openai.com/?model=gpt-4&prompt=${encodeURIComponent(`For the property
${address}
provide the following information
What is the latest sale price and sale date for this property?
Gross Living Area: What is the property's gross living area?
Acreage: What is the property's acreage?
Please provide a photo of this home.
What is the Style of this home?
What is the year built for this home?
Was this property recently rennovated if so in what year?
How many full Baths does this home have?
How many half Baths does this home have?
How many kitchens Baths does this home have?
How many bedrooms does this home have?
How many Garages does this home have?
Does this home have an in-ground swimming pool?
Does this home have a Basement? If so is it: (Full finished/ Partial Finished / Unfinished) and estimate the square 13) What is the conditon of this home?
What is the Condition / Grade of this home?
`)}`, '_blank');
    handleClose();
  }, [row]);

  const handleWebSearch = useCallback(() => {
    const address = `${row.Address}, ${row.Town}, ${STATE} ${row.ZipCode}`;
    logger.log('Web Search Address String:', address);
    window.open(`https://www.google.com/search?q=${encodeURIComponent(address)}`, '_blank');
    handleClose();
  }, [row]);

  const handleCopyAddress = useCallback(async () => {
    const address = `${row.Address}, ${row.Town}, ${STATE} ${row.ZipCode}`;
    try {
      await navigator.clipboard.writeText(address);
      toast.success('Address copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = address;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Address copied to clipboard!');
    }
    handleClose();
  }, [row]);

  const handleRequestPropertyInfo = useCallback(async () => {
    logger.log('Request Property Info - Address:', row.Address);
    logger.log('Request Property Info - Parcel ID:', row.parcel_id);
    logger.log('Request Property Info - Description:', row.Description);
    logger.log('Request Property Info - Tax Year:', row.TaxYear);

    // Check if the data analysis task is already completed by calling the API
    try {
      const taskStatusData = {
        parcel_id: row.parcel_id,
        review_type: 'DataCleaning',
        year: row.TaxYear
      };
      
      const response = await axiosInstance.post('/check_task_status', taskStatusData);
      logger.log('response from check_task_status', response)
      
      if (response.data.success) {
        const isTaskCompleted = response.data.is_completed;
        
        if (isTaskCompleted) {
          // Show confirmation dialog asking if they want to request again
          onShowPropertyInfoConfirm(row, handleClose);
          return;
        }
      } else {
        logger.error('Error checking task status:', response.data.error);
        toast.error('Failed to check task status');
        return;
      }
    } catch (error) {
      logger.error('Error checking task status:', error);
      return;
    }

    // If not completed, proceed with the request
    onRequestPropertyInfo(row, handleClose);
  }, [row, onRequestPropertyInfo, onShowPropertyInfoConfirm]);

  const handleDataNotAvailable = useCallback(() => {
    logger.log('Data Not Available - Address:', row.Address);
    logger.log('Data Not Available - Parcel ID:', row.parcel_id);
    logger.log('Data Not Available - Tax Year:', row.TaxYear);
    
    onDataNotAvailable(row, handleClose);
  }, [row, onDataNotAvailable]);
  return (
    <>
      <div 
        className="flex items-center gap-1 cursor-pointer hover:text-blue-500 w-full"
        onClick={handleClick}
        style={{ 
          textDecoration: 'underline',
          outline: anchorEl ? '1px solid #1976d2' : 'none',
          outlineOffset: '-1px',
          width: '100%'
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
            width: '100%'
          }}
        >
          {value}
        </span>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleCopyAddress}>
          <ContentCopyIcon sx={{ fontSize: 16, mr: 1 }} />
          Copy Address
        </MenuItem>
        <MenuItem onClick={handleAskAI}>
          <SmartToyOutlinedIcon sx={{ fontSize: 16, mr: 1 }} />
          Ask AI for Inventory
        </MenuItem>
        <MenuItem onClick={handleWebSearch}>
          <PublicIcon sx={{ fontSize: 16, mr: 1 }} />
          Search on Web
        </MenuItem>
        <MenuItem onClick={handleRequestPropertyInfo} disabled={true}>
          <InfoIcon sx={{ fontSize: 16, mr: 1 }} />
          Request Property Info
        </MenuItem>
        <MenuItem onClick={handleDataNotAvailable}>
          <CloseIcon sx={{ fontSize: 16, mr: 1 }} />
          Data not available
        </MenuItem>
      </Menu>
    </>
  );
});

const DATA_ANALYSIS_LOADING_COLUMN = {
  field: '__loading__',
  headerName: ' ',
  flex: 1,
  minWidth: 400,
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  editable: false,
};

const EMPTY_FILTER_MODEL = { items: [], logicOperator: 'and' };

function sanitizeExportFileNamePart(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function getDataAnalysisExportFileName(muniString, activeTab) {
  const muniPart = sanitizeExportFileNamePart(muniString) || 'unknown';
  const tabSuffix = {
    clients: 'clients',
    salesComps: 'salescomps',
    equityComps: 'equitycomps',
    all: 'all',
  }[activeTab] ?? 'clients';

  return `${muniPart}_data_cleaning_report_${tabSuffix}`;
}

const CustomToolbar = ({ exportFileName }) => {
  return (
    <Toolbar className="flex justify-between">
      <div className="flex gap-2">
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarExportButton options={{ fileName: exportFileName, delimiter: ',' }} />
      </div>
      <CustomQuickFilter debounceMs={500} />
    </Toolbar>
  );
};

// Extract SummaryStats into its own component
const SummaryStats = memo(({ 
  descriptionCounts, 
  selectedDescription, 
  onRowClick,
  isGroupedView,
  totalRows,
  clientCount,
  activeTab,  // Add activeTab prop
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedMunicipality, setExpandedMunicipality] = useState(null);
  const [showAllCounts, setShowAllCounts] = useState(false);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const summaryRef = useRef(null);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (summaryRef.current && !summaryRef.current.contains(event.target)) {
        setIsExpanded(false);
        setShowAllCounts(false);
        setExpandedMunicipality(null);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleClearFilter = useCallback((e) => {
    e.stopPropagation();
    onRowClick(selectedDescription);
  }, [selectedDescription, onRowClick]);

  const handleMunicipalityClick = useCallback((municipality) => {
    setExpandedMunicipality(prev => prev === municipality ? null : municipality);
  }, []);

  const handleTotalClick = useCallback(() => {
    setShowAllCounts(prev => !prev);
    // Only clear filters if a filter is actually active
    if (selectedDescription && onRowClick) {
      onRowClick(null);
    }
  }, [onRowClick, selectedDescription]);

  // Calculate all individual counts across municipalities
  const allIndividualCounts = useMemo(() => {
    const counts = {};
    descriptionCounts.forEach(row => {
      if (row.items) {
        row.items.forEach(item => {
          const count = item.count || '0';
          counts[item.description] = (counts[item.description] || 0) + parseInt(count.toString().replace(/,/g, '') || '0');
        });
      }
    });
    return Object.entries(counts)
      .map(([description, count]) => {
        const percentage = activeTab === 'clients' 
          ? (clientCount ? Math.round((count / clientCount) * 100) : 0)  // Use clientCount for clients tab
          : (totalRows ? Math.round((count / totalRows) * 100) : 0);     // Use totalRows for other tabs
        return {
          description,
          count: count.toLocaleString(),
          percentage
        };
      })
      .sort((a, b) => {
        const countA = parseInt(a.count.toString().replace(/,/g, '') || '0');
        const countB = parseInt(b.count.toString().replace(/,/g, '') || '0');
        return countB - countA;
      });
  }, [descriptionCounts, clientCount, totalRows, activeTab]);

  // Updated handler for filtering that manages local UI state and calls onRowClick
  const handleFilterClick = (description, municipality = null) => {
    setShowAllCounts(false); // Force hide flat list when filtering
    if (isGroupedView) {
      if (municipality) {
        // If municipality is provided, select that specific municipality
        setExpandedMunicipality(municipality);
        setSelectedMunicipality(municipality);
      } else {
        // If no municipality provided (clicking from TOTAL display), don't select any specific municipality
        // This will show the filter across all municipalities with "Total" as header
        setExpandedMunicipality(null);
        setSelectedMunicipality(null);
      }
    }
    onRowClick({ description, municipality });
  };

  // Local filteredDescriptionCounts logic
  const filteredDescriptionCounts = useMemo(() => {
    if (selectedDescription) {
      if (isGroupedView && selectedMunicipality) {
        // Only show the selected field for the selected municipality
        const filtered = descriptionCounts
          .filter(row => row.municipality === selectedMunicipality)
          .map(row => {
            if (!row.items) return row;
            const matchingItems = row.items.filter(item => item.description === selectedDescription);
            return {
              ...row,
              items: matchingItems
            };
          })
          .filter(row => row.items && row.items.length > 0);
        return filtered;
      } else if (isGroupedView) {
        // Show the selected field across all municipalities (when filtering from TOTAL)
        const filtered = descriptionCounts.map((row) => {
          if (!row.items) return row;
          const matchingItems = row.items.filter(item => item.description === selectedDescription);
          return {
            ...row,
            items: matchingItems
          };
        }).filter(row => row.items && row.items.length > 0);
        return filtered;
      } else {
        const filtered = descriptionCounts.filter(row => row.description === selectedDescription);
        return filtered;
      }
    }
    const result = isGroupedView
      ? descriptionCounts
      : descriptionCounts.filter(row => row.description !== 'Total');
    return result;
  }, [descriptionCounts, selectedDescription, isGroupedView, selectedMunicipality]);

  // Helper to humanize summary descriptions using columnHeaderMappings
  function humanizeDescription(description) {
    // Handles "Missing Sqft", "Extreme YearBuilt", etc.
    const match = description.match(/^(Missing|Extreme) (.+)$/);
    if (match) {
      const [, prefix, field] = match;
      // Try to map field to human label
      const label = columnHeaderMappings[field] || field;
      return `${prefix} ${label}`;
    }
    // If description is just a field name, map it
    return columnHeaderMappings[description] || description;
  }

  return (
    <div className="relative" style={{ minWidth: 200 }} ref={summaryRef}>
      <Button
        variant="outlined"
        onClick={handleToggle}
        endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          height: 32,
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          textTransform: 'none',
          borderColor: 'rgba(0, 0, 0, 0.23)',
          '&:hover': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <Typography variant="subtitle1" sx={{ fontSize: 14 }}>Issues Summary</Typography>
      </Button>

      <Collapse in={isExpanded} className="absolute top-full mt-2 right-0 z-10 w-max">
        <TableContainer 
          component={Paper} 
          className="rounded shadow-lg"
          sx={{ 
            width: 240,
            maxHeight: '60vh',
            overflow: 'auto',
            '& .MuiTable-root': {
              tableLayout: 'fixed'
            }
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '60%', whiteSpace: 'nowrap', boxSizing: 'border-box', overflow: 'hidden', textOverflow: 'ellipsis', pl: 1 }}>Description</TableCell>
                <TableCell align="right" sx={{ width: '40%', pl: 0, pr: 1, boxSizing: 'border-box', minWidth: 100, maxWidth: 100, whiteSpace: 'nowrap' }}>Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Only show the total dropdown section if no filter is active */}
              {!selectedDescription && isGroupedView && (
                <TableRow 
                  onClick={handleTotalClick}
                  sx={{ 
                    backgroundColor: 'rgba(0, 0, 0, 0.02)', 
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <TableCell component="th" scope="row" sx={{ width: '60%', whiteSpace: 'nowrap', boxSizing: 'border-box', overflow: 'hidden', textOverflow: 'ellipsis', pl: 1 }}>
                    <div className="flex items-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                      {showAllCounts ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      <span className="ml-1" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>Total</span>
                    </div>
                  </TableCell>
                  <TableCell align="right" sx={{ width: '40%', pl: 0, pr: 1, boxSizing: 'border-box', fontWeight: 'bold', minWidth: 100, maxWidth: 100, whiteSpace: 'nowrap' }}>
                    {(() => {
                      const subtotal = descriptionCounts
                        .filter(row => row.description !== 'Total')
                        .reduce((sum, row) => {
                          const n = Number(String(row.total).replace(/,/g, ''));
                          return sum + (isNaN(n) ? 0 : n);
                        }, 0);
                      const percentage = activeTab === 'clients'
                        ? (clientCount ? Math.round((subtotal / clientCount) * 100) : 0)  // Use clientCount for clients tab
                        : (totalRows ? Math.round((subtotal / totalRows) * 100) : 0);     // Use totalRows for other tabs
                      return (
                        <span>
                          {subtotal.toLocaleString()}
                          <span style={{ fontSize: '0.85em', marginLeft: '4px', opacity: 0.7 }}>({percentage}%)</span>
                        </span>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              )}
              {showAllCounts && allIndividualCounts.map((item) => (
                <TableRow 
                  key={item.description}
                  onClick={() => {
                    setShowAllCounts(false);
                    handleFilterClick(item.description);
                  }}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    },
                    backgroundColor: selectedDescription === item.description 
                      ? 'rgba(25, 118, 210, 0.08)'
                      : 'inherit'
                  }}
                >
                  <TableCell component="th" scope="row" sx={{ pl: 2, width: '60%', whiteSpace: 'nowrap', pr: 1, boxSizing: 'border-box', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <div className="flex items-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{humanizeDescription(item.description)}</span>
                      {selectedDescription === item.description && (
                        <Tooltip title="Clear Filter">
                          <IconButton 
                            size="small" 
                            onClick={handleClearFilter}
                            sx={{ 
                              color: 'error.main',
                              ml: 1,
                              flexShrink: 0,
                              '&:hover': {
                                backgroundColor: 'rgba(211, 47, 47, 0.04)'
                              }
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell align="right" sx={{ width: '40%', pl: 0, pr: 1, boxSizing: 'border-box', minWidth: 100, maxWidth: 100, whiteSpace: 'nowrap' }}>
                    <span>
                      {item.count}
                      <span style={{ fontSize: '0.85em', marginLeft: '4px', opacity: 0.7 }}>({item.percentage}%)</span>
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {filteredDescriptionCounts
                .filter(row => row.municipality || (row.description && row.description !== 'Total'))
                .map((row) => {
                  const onlyOneGroupSelected = selectedMunicipality && isGroupedView;
                  const isTotalFilter = selectedDescription && !selectedMunicipality && isGroupedView;
                  
                  // Special case: When filtering from TOTAL display, show "Total" as header and all municipalities
                  if (isTotalFilter) {
                    // Only process this once for the first row
                    if (row === filteredDescriptionCounts[0]) {
                      // Calculate the total count across all municipalities for this description
                      const totalCount = filteredDescriptionCounts.reduce((sum, municipalityRow) => {
                        if (!municipalityRow.items) return sum;
                        const matchingItem = municipalityRow.items.find(item => item.description === selectedDescription);
                        if (matchingItem) {
                          return sum + parseInt(matchingItem.count.toString().replace(/,/g, '') || '0');
                        }
                        return sum;
                      }, 0);
                      
                      // Calculate percentage based on the total
                      const percentage = activeTab === 'clients'
                        ? (clientCount ? Math.round((totalCount / clientCount) * 100) : 0)
                        : (totalRows ? Math.round((totalCount / totalRows) * 100) : 0);
                      
                      return (
                        <React.Fragment key="total-filter-header">
                          <TableRow
                            sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}
                          >
                            <TableCell colSpan={2}>
                              <div className="flex items-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                <span className="ml-1 font-bold" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>Total</span>
                              </div>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            onClick={() => handleFilterClick(selectedDescription)}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                              backgroundColor: 'rgba(25, 118, 210, 0.08)'
                            }}
                          >
                            <TableCell sx={{ pl: 2, width: '60%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              <div className="flex items-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{humanizeDescription(selectedDescription)}</span>
                                <Tooltip title="Clear Filter">
                                  <IconButton
                                    size="small"
                                    onClick={handleClearFilter}
                                    sx={{
                                      color: 'error.main',
                                      ml: 1,
                                      flexShrink: 0,
                                      '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' }
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            </TableCell>
                            <TableCell align="right" sx={{ width: '40%', pl: 0, pr: 1, minWidth: 100, maxWidth: 100, whiteSpace: 'nowrap' }}>
                              <span>
                                {totalCount.toLocaleString()}
                                <span style={{ fontSize: '0.85em', marginLeft: '4px', opacity: 0.7 }}>({percentage}%)</span>
                              </span>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    }
                    // Skip processing other rows when in TOTAL filter mode
                    return null;
                  }
                  
                  return (
                    <React.Fragment key={row.municipality || row.description}>
                      {onlyOneGroupSelected ? (
                        <>
                          <TableRow
                            onClick={() => handleMunicipalityClick(row.municipality)}
                            sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)', cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                          >
                            <TableCell colSpan={2}>
                              <div className="flex items-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                {expandedMunicipality === row.municipality ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                <span className="ml-1 font-bold" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.municipality}</span>
                              </div>
                            </TableCell>
                          </TableRow>
                          {expandedMunicipality === row.municipality && row.items.map((item) => {
                            const count = parseInt(item.count.toString().replace(/,/g, '') || '0');
                            // For percentage calculation, we need the total data entries in this municipality, not just the ones with issues
                            // The municipality total should be the total number of properties in this municipality that have any data issues
                            const municipalityTotal = row.totalCount || parseInt(row.total.toString().replace(/,/g, '') || '0');
                            
                            // For individual items within a municipality, calculate percentage based on municipality's total issues
                            // This shows what percentage of the municipality's problematic properties have this specific issue
                            // Always use municipality total for individual items, regardless of tab
                            const percentage = municipalityTotal ? Math.round((count / municipalityTotal) * 100) : 0;
                            
                            return (
                              <TableRow
                                key={item.description}
                                onClick={() => handleFilterClick(item.description, row.municipality)}
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                  backgroundColor: selectedDescription === item.description
                                    ? 'rgba(25, 118, 210, 0.08)'
                                    : 'inherit'
                                }}
                              >
                                <TableCell sx={{ pl: 2, width: '60%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  <div className="flex items-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{humanizeDescription(item.description)}</span>
                                    {selectedDescription === item.description && (
                                      <Tooltip title="Clear Filter">
                                        <IconButton
                                          size="small"
                                          onClick={handleClearFilter}
                                          sx={{
                                            color: 'error.main',
                                            ml: 1,
                                            flexShrink: 0,
                                            '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' }
                                          }}
                                        >
                                          <CloseIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell align="right" sx={{ width: '40%', pl: 0, pr: 1, minWidth: 100, maxWidth: 100, whiteSpace: 'nowrap' }}>
                                  <span>
                                    {item.count}
                                    <span style={{ fontSize: '0.85em', marginLeft: '4px', opacity: 0.7 }}>({percentage}%)</span>
                                  </span>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </>
                      ) : (
                        <>
                          {row.municipality ? (
                            <>
                              <TableRow 
                                onClick={() => handleMunicipalityClick(row.municipality)}
                                sx={{ 
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                  }
                                }}
                              >
                                <TableCell component="th" scope="row" sx={{ width: '60%', whiteSpace: 'nowrap', boxSizing: 'border-box', overflow: 'hidden', textOverflow: 'ellipsis', pl: 1 }}>
                                  <div className="flex items-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                    {expandedMunicipality === row.municipality ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    <span className="ml-1 font-bold" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.municipality}</span>
                                  </div>
                                </TableCell>
                                <TableCell align="right" sx={{ width: '40%', pl: 0, pr: 1, fontWeight: 'bold', boxSizing: 'border-box', minWidth: 100, maxWidth: 100, whiteSpace: 'nowrap' }}>
                                  {(() => {
                                    const total = parseInt(row.total.toString().replace(/,/g, '') || '0');
                                    const percentage = activeTab === 'clients'
                                      ? (clientCount ? Math.round((total / clientCount) * 100) : 0)  // Use clientCount for clients tab
                                      : (totalRows ? Math.round((total / totalRows) * 100) : 0);     // Use totalRows for other tabs
                                    return (
                                      <span>
                                        {row.total}
                                        <span style={{ fontSize: '0.85em', marginLeft: '4px', opacity: 0.7 }}>({percentage}%)</span>
                                      </span>
                                    );
                                  })()}
                                </TableCell>
                              </TableRow>
                              {expandedMunicipality === row.municipality && (
                                row.items.map((item) => {
                                  const count = parseInt(item.count.toString().replace(/,/g, '') || '0');
                                  // For percentage calculation, we need the total data entries in this municipality, not just the ones with issues
                                  // The municipality total should be the total number of properties in this municipality that have any data issues
                                  const municipalityTotal = row.totalCount || parseInt(row.total.toString().replace(/,/g, '') || '0');
                                  
                                  // For individual items within a municipality, calculate percentage based on municipality's total issues
                                  // This shows what percentage of the municipality's problematic properties have this specific issue
                                  // Always use municipality total for individual items, regardless of tab
                                  const percentage = municipalityTotal ? Math.round((count / municipalityTotal) * 100) : 0;
                                  
                                  return (
                                    <TableRow 
                                      key={item.description}
                                      onClick={() => handleFilterClick(item.description, row.municipality)}
                                      sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': {
                                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        },
                                        backgroundColor: selectedDescription === item.description 
                                          ? 'rgba(25, 118, 210, 0.08)'
                                          : 'inherit'
                                      }}
                                    >
                                      <TableCell component="th" scope="row" sx={{ pl: 2, width: '60%', whiteSpace: 'nowrap', pr: 1, boxSizing: 'border-box', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        <div className="flex items-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{humanizeDescription(item.description)}</span>
                                          {selectedDescription === item.description && (
                                            <Tooltip title="Clear Filter">
                                              <IconButton 
                                                size="small" 
                                                onClick={handleClearFilter}
                                                sx={{ 
                                                  color: 'error.main',
                                                  ml: 1,
                                                  flexShrink: 0,
                                                  '&:hover': {
                                                    backgroundColor: 'rgba(211, 47, 47, 0.04)'
                                                  }
                                                }}
                                              >
                                                <CloseIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell align="right" sx={{ width: '40%', pl: 0, pr: 1, boxSizing: 'border-box', minWidth: 100, maxWidth: 100, whiteSpace: 'nowrap' }}>
                                        <span>
                                          {item.count}
                                          <span style={{ fontSize: '0.85em', marginLeft: '4px', opacity: 0.7 }}>({percentage}%)</span>
                                        </span>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                              )}
                            </>
                          ) : (
                            <TableRow 
                              onClick={() => handleFilterClick(row.description)}
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                },
                                backgroundColor: selectedDescription === row.description 
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'inherit'
                              }}
                            >
                              <TableCell component="th" scope="row" sx={{ width: '60%', whiteSpace: 'nowrap', pr: 1, boxSizing: 'border-box', overflow: 'hidden', textOverflow: 'ellipsis', pl: 1 }}>
                                <div className="flex items-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{humanizeDescription(row.description)}</span>
                                  {selectedDescription === row.description && (
                                    <Tooltip title="Clear Filter">
                                      <IconButton 
                                        size="small" 
                                        onClick={handleClearFilter}
                                        sx={{ 
                                          color: 'error.main',
                                          ml: 1,
                                          flexShrink: 0,
                                          '&:hover': {
                                            backgroundColor: 'rgba(211, 47, 47, 0.04)'
                                          }
                                        }}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell align="right" sx={{ width: '40%', pl: 0, pr: 1, boxSizing: 'border-box', minWidth: 100, maxWidth: 100, whiteSpace: 'nowrap' }}>
                                {(() => {
                                  const count = parseInt(row.count.toString().replace(/,/g, '') || '0');
                                  const percentage = activeTab === 'clients'
                                    ? (clientCount ? Math.round((count / clientCount) * 100) : 0)  // Use clientCount for clients tab
                                    : (totalRows ? Math.round((count / totalRows) * 100) : 0);     // Use totalRows for other tabs
                                  return (
                                    <span>
                                      {row.count}
                                      <span style={{ fontSize: '0.85em', marginLeft: '4px', opacity: 0.7 }}>({percentage}%)</span>
                                    </span>
                                  );
                                })()}
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </div>
  );
});

// Add this new component for the Style select editor
const StyleSelectEditor = (props) => {
  const { value, api, field } = props;

  const handleChange = (event) => {
    api.setEditCellValue({ id: props.id, field, value: event.target.value });
  };

  return (
    <Select
      value={value || ''}
      onChange={handleChange}
      fullWidth
      native
      autoFocus
    >
      <option value="" disabled>Select</option>
      {styleOptions.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </Select>
  );
};

// Add this new component for the Pool select editor
const PoolSelectEditor = (props) => {
  const { value, api, field } = props;

  const handleChange = (event) => {
    // Convert "Yes"/"No" to 1/0 for database storage, empty string to null
    let binaryValue;
    if (event.target.value === 'Yes') {
      binaryValue = 1;
    } else if (event.target.value === 'No') {
      binaryValue = 0;
    } else {
      // Empty string or "Select" option - set to null to indicate no selection
      binaryValue = null;
    }
    api.setEditCellValue({ id: props.id, field, value: binaryValue });
  };

  // Convert 1/0 to "Yes"/"No" for display, but show empty for null/undefined/empty string
  const displayValue = value === 1 || value === '1' ? 'Yes' : 
                      value === 0 || value === '0' ? 'No' : '';

  return (
    <Select
      value={displayValue}
      onChange={handleChange}
      fullWidth
      native
      autoFocus
    >
      <option value="">Select</option>
      <option value="No">No</option>
      <option value="Yes">Yes</option>
    </Select>
  );
};

// Memoized DataGrid component to prevent unnecessary re-renders when bulk menu state changes
const MemoizedDataGrid = memo(({ apiRef, dataGridProps, isCellEditable, handleTabNavigation }) => {
  return (
    <DataGridPro 
      apiRef={apiRef}
      {...dataGridProps}
      rowSpacingType="border"
      rowSpacing={{ top: 0, bottom: 0 }}
      disableColumnFilter
      columnBufferPx={150}
      rowBufferPx={200}
      getCellTabIndex={(params) => {
        // only put your editable/bad-data cells into the tab order
        if (isCellEditable(params)) {
          return 0;    // this cell becomes tabbable
        }
        return -1;     // everything else is skipped
      }}
      onCellKeyDown={(params, event) => {
        if (event.key === 'Tab') {
          event.preventDefault();
          handleTabNavigation(params, event);
        }
      }}
      sx={{
        '& .MuiDataGrid-cell.missing-data-cell': {
          border: '1px solid red',
          boxSizing: 'border-box',
          backgroundColor: 'rgba(255, 255, 200, 0.3) !important',
        },
        '& .MuiDataGrid-cell.fixed-data-cell': {
          border: '2px solid #4caf50 !important',
          boxSizing: 'border-box',
          backgroundColor: 'transparent !important',
          color: '#2e7d32 !important',
          fontWeight: 500,
          '&:hover': {
            backgroundColor: 'rgba(76, 175, 80, 0.05) !important',
          }
        },
        // Horizontal dedupe
        '& .MuiDataGrid-row .MuiDataGrid-cell.missing-data-cell + .MuiDataGrid-cell.missing-data-cell': {
          borderLeft: 'none !important',
        },
        '& .MuiDataGrid-row .MuiDataGrid-cell.fixed-data-cell + .MuiDataGrid-cell.fixed-data-cell': {
          borderLeft: 'none !important',
        },
        // Vertical dedupe
        '& .MuiDataGrid-cell.no-border-top': {
          borderTop: 'none !important',
        },
        '& .MuiDataGrid-cell.no-border-bottom': {
          borderBottom: 'none !important',
        },
        '& .MuiDataGrid-cell:focus': {
          outline: 'none !important',
        },
        '& .MuiDataGrid-cell:focus-within': {
          outline: 'none !important',
        },
        '& .MuiDataGrid-cell--editing': {
          backgroundColor: 'transparent !important',
          border: '2px solid #ffd700 !important',
          boxSizing: 'border-box',
        }
      }}
    />
  );
});


// Field type definitions are now imported from columnDefinitions.js

/** Default tab is clients; align filter model (and strip stale Sales/Equity tab filters). Returns null if already valid. */
function ensureClientsTabFilterModel(model) {
  const items = Array.isArray(model?.items) ? model.items : [];
  const hasClient = items.some(
    (item) =>
      item?.field === 'Client' &&
      item?.operator === '=' &&
      String(item?.value) === '1'
  );
  if (hasClient) return null;
  const nextItems = items.filter(
    (item) =>
      item.field !== 'Client' &&
      item.field !== 'IsSalesComp' &&
      item.field !== 'IsAssessmentComp'
  );
  nextItems.push({ id: 'tab-client', field: 'Client', operator: '=', value: '1' });
  return { items: nextItems, logicOperator: model?.logicOperator || 'and' };
}

/** When "Return All" is in data flags, do not send outlier fields to /flag_bad_data (matches BadDataFlagsSelector). */
function outlierFieldsForBadDataRequest(flags) {
  if ((flags?.data || []).includes('Return All')) return [];
  return flags?.outliers || [];
}

// Should just rename this to DataAnalaysisGridContainer instead, is being custom built for this now.
// this is built with the new experimental feature editing api. May be subject to break / change so monitor.
const DataAnalysisDatagrid = ({ 
  columns, 
  rows, 
  pageSizeOptions = [5, 10, 20], 
  localeText,
  columnFilter = {},
  height,
  initialFilterModel = null,
  isUpdating = false,
  totalRows: initialTotalRows = 0,
  clientCount: initialClientCount = 0,
  countySpecificBadDataFlags = null
}) => {
  // Store selections
  const negotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const setDataAnalysisResult = usePersistedDataStore((state) => state.setDataAnalysisResult);
  const setDataAnalysisColumns = usePersistedDataStore((state) => state.setDataAnalysisColumns);
  const setDataAnalysisFilterModel = usePersistedDataStore((state) => state.setDataAnalysisFilterModel);
  const setIsFetching = usePersistedDataStore((state) => state.setIsFetching);
  const isEnrichingClientCases = usePersistedDataStore((state) => state.isEnrichingClientCases);
  const [totalRows, setTotalRows] = useState(initialTotalRows);
  const [clientCount, setClientCount] = useState(initialClientCount);

  // Sync props to state when they change. Stupid flow but a bandaid fix for now.
  useEffect(() => {
    setTotalRows(initialTotalRows);
    setClientCount(initialClientCount);
  }, [initialTotalRows, initialClientCount]);

  // Get user object from UserContext
  const { user } = useUser();

  // State to track rows that have been requested for property info (to hide them)
  const [removedRows, setRemovedRows] = useState(new Set());

  // Use persisted state for bad data flags
  const badDataFlags = usePersistedDataStore((state) => state.badDataFlags);
  const setBadDataFlags = usePersistedDataStore((state) => state.setBadDataFlags);
  const flagType = usePersistedDataStore((state) => state.flagType);

  // Starting state logic 
  const [activeTab, setActiveTab] = useState(() => {
    // default to clients tab, removed prior "intelligent" selection.
    return 'clients';
  });
  const [selectedDescription, setSelectedDescription] = useState(null);
  const [hasEdits, setHasEdits] = useState(false);

  // Property info request confirmation states  
  const [propertyInfoConfirmOpen, setPropertyInfoConfirmOpen] = useState(false);
  const [pendingPropertyInfoRequest, setPendingPropertyInfoRequest] = useState(null);
  const [loadingTaskDetails, setLoadingTaskDetails] = useState(false);
  
  // Selection model for multi-select operations
  const [selectionModel, setSelectionModel] = useState(createEmptyRowSelection);
  const selectedRowIds = useMemo(() => getSelectedRowIds(selectionModel), [selectionModel]);
  
  // Loading state for flag operations
  const [isFlaggingData, setIsFlaggingData] = useState(false);
  
  // Dropdown menu state for bulk operations
  const [bulkMenuAnchorEl, setBulkMenuAnchorEl] = useState(null);
  const bulkMenuOpen = Boolean(bulkMenuAnchorEl);

  // Flag bad sale dialog state
  const [flagBadSaleDialogOpen, setFlagBadSaleDialogOpen] = useState(false);
  const [isFlaggingBadSale, setIsFlaggingBadSale] = useState(false);

  const apiRef = useGridApiRef();
  const unsavedChangesRef = useRef({
    unsavedRows: {},
    rowsBeforeChange: {}
  });

  // Local state for BadDataFlagsSelector (syncWithStore false) — seed from store so InvokeModels / API see the same flags
  const [localBadDataFlags, setLocalBadDataFlags] = useState(() => {
    const s = usePersistedDataStore.getState().badDataFlags;
    return {
      data: Array.isArray(s?.data) ? [...s.data] : [],
      outliers: Array.isArray(s?.outliers) ? [...s.outliers] : [],
    };
  });
  const [localFlagType, setLocalFlagType] = useState('data');
  const [isResettingFlags, setIsResettingFlags] = useState(false);
  const prevCountyFlagsRef = useRef(null);

  // Determine current field list based on type
  const fieldList = localFlagType === 'data' ? AVAILABLE_BAD_DATA_FIELDS : OUTLIER_FIELDS;

  // Map to hold selections per type
  const selectedFlags = localBadDataFlags[localFlagType] || [];
  
  // Unique fields provided to selector, mapped to readable names for display
  const uniqueFields = useMemo(() => fieldList, [fieldList]);
  // Map field keys to readable names for display in selector
  const fieldDisplayName = (field) => columnHeaderMappings[field] || (field === 'PhotoURL' ? 'Photo' : field);

  // Keep global store aligned with the grid selector (InvokeModels reads store.badDataFlags).
  // Do not push county props into the store here: object reference !== always fired and wiped user outliers.
  useEffect(() => {
    if (isEqual(localBadDataFlags, badDataFlags)) return;
    setBadDataFlags(localBadDataFlags);
  }, [localBadDataFlags, badDataFlags, setBadDataFlags]);

  const isReturnAllBadData = useMemo(
    () =>
      (localBadDataFlags?.data || []).includes('Return All') ||
      (badDataFlags?.data || []).includes('Return All'),
    [localBadDataFlags?.data, badDataFlags?.data]
  );

  // Detect when flags are being reset due to municipality changes -- need to see if this is really necesssary in future.
  useEffect(() => {
    logger.log('the internal datagridcontiaingerv2 useeffect with timeout + reset is running.')
    if (countySpecificBadDataFlags) {
      logger.log(' i think this is not needed / never runs?')
      // Check if this is actually a new set of flags (not just a re-render)
      const currentFlagsStr = JSON.stringify(countySpecificBadDataFlags);
      const prevFlagsStr = prevCountyFlagsRef.current ? JSON.stringify(prevCountyFlagsRef.current) : null;
      
      if (prevFlagsStr && currentFlagsStr !== prevFlagsStr) {
        // Show resetting indicator when county-specific flags actually change
        setIsResettingFlags(true);
        
        // Update local flags
        setLocalBadDataFlags(countySpecificBadDataFlags);
        
        // Hide the indicator after a short delay
        const timer = setTimeout(() => {
          setIsResettingFlags(false);
        }, 500);
        
        return () => clearTimeout(timer);
      } else if (!prevFlagsStr) {
        const storeFlags = usePersistedDataStore.getState().badDataFlags;
        const storeHasSelection =
          (storeFlags?.data?.length > 0) || (storeFlags?.outliers?.length > 0);
        if (storeHasSelection && !isEqual(storeFlags, countySpecificBadDataFlags)) {
          setLocalBadDataFlags({
            data: [...(storeFlags.data || [])],
            outliers: [...(storeFlags.outliers || [])],
          });
        } else {
          setLocalBadDataFlags(countySpecificBadDataFlags);
        }
      }
      
      // Update the ref for next comparison
      prevCountyFlagsRef.current = countySpecificBadDataFlags;
    }
  }, [countySpecificBadDataFlags]);

  // Helper function to check if a description contains an exact field issue (not substring)
  const hasFieldIssue = useCallback((description, fieldName) => {
    if (!description || !fieldName) return false;
    // Explicitly match the exact field for HalfBath to avoid Bath false positives
    if (fieldName === 'HalfBath') {
      return description.includes('(HalfBath_NA)') || description.includes('(HalfBath_EXTREME)');
    }
    // For all other fields, ensure we do not match substrings (e.g., Bath in HalfBath)
    const regexNA = new RegExp(`\\(${fieldName}_NA\\)`, 'g');
    const regexEXTREME = new RegExp(`\\(${fieldName}_EXTREME\\)`, 'g');
    return regexNA.test(description) || regexEXTREME.test(description);
  }, []);

  // ALWAYS_EDITABLE_FIELDS is now imported from columnDefinitions.js
  const isCellEditable = useCallback((params) => {
    // PCC field should never be editable
    if (params.field === 'PCC') return false;
    // If user has already confirmed editing for this cell, allow regardless of validation
    if (overrideEditFieldsRef.current.has(`${params.id}-${params.field}`)) {
      return true;
    }
    const fieldName = params.field;
    // Always allow editing of HalfBath and the listed fields
    if (ALWAYS_EDITABLE_FIELDS.includes(fieldName)) return true;
    const description = params.row.Description || '';
    const hasIssue = isReturnAllBadData ? false : hasFieldIssue(description, fieldName);
    return hasIssue;
  }, [hasFieldIssue, isReturnAllBadData]);

  // Memoize columns with filter applied if provided
  const memoizedColumns = useMemo(() => {
    let baseCols = columns;
    if (columnFilter && Object.keys(columnFilter).length > 0) {
      baseCols = columns.filter(col => columnFilter[col.field]).map(col => ({
        ...col,
        headerName: columnFilter[col.field] || col.headerName
      }));
    }

    // Column ordering is handled in columnDefinitions.js (DATA_ANALYSIS_COLUMN_ORDER).
    // Remove logic for PCC after Address (no longer needed)

    // Ensure Description column is present, filterable, and hidden
    const hasDescription = baseCols.some(col => col.field === 'Description');
    if (!hasDescription) {
      baseCols = [
        ...baseCols,
        {
          field: 'Description',
          headerName: 'Description',
          filterable: true,
          hide: true,
          width: 150,
        }
      ];
    } else {
      baseCols = baseCols.map(col =>
        col.field === 'Description'
          ? { ...col, filterable: true, hide: true }
          : col
      );
    }

    return baseCols;
  }, [columns, columnFilter]);

  const isGridLoading = isUpdating || isFlaggingData || isEnrichingClientCases;
  const hasDataColumns = columns.length > 0;

  const gridDisplayColumns = useMemo(() => {
    if (isGridLoading && !hasDataColumns) {
      return [DATA_ANALYSIS_LOADING_COLUMN];
    }
    return memoizedColumns;
  }, [isGridLoading, hasDataColumns, memoizedColumns]);

  const exportFileName = useMemo(
    () => getDataAnalysisExportFileName(negotiationObj?.muniString, activeTab),
    [negotiationObj?.muniString, activeTab],
  );

  const ToolbarSlot = useCallback(
    () => <CustomToolbar exportFileName={exportFileName} />,
    [exportFileName],
  );

  // Memoize pageSizeOptions to prevent recreating the array
  const memoizedPageSizeOptions = useMemo(() => pageSizeOptions, [pageSizeOptions]);

  // Initialize filter model with proper structure (default UI tab is clients — see activeTab initial state)
  const [filterModel, setFilterModel] = useState(() => {
    if (!initialFilterModel) {
      return {
        items: [{ id: 'tab-client', field: 'Client', operator: '=', value: '1' }],
        logicOperator: 'and'
      };
    }
    return ensureClientsTabFilterModel(initialFilterModel) ?? initialFilterModel;
  });

  useEffect(() => {
    if (activeTab !== 'clients') return;
    const next = ensureClientsTabFilterModel(filterModel);
    if (!next) return;
    setFilterModel(next);
    setDataAnalysisFilterModel(next);
  }, [activeTab, filterModel, setDataAnalysisFilterModel]);

  // Controlled column visibility merged with defaults (async column list after fetch would stay "all visible" if we only used initialState).
  const [columnVisibilityModelState, setColumnVisibilityModelState] = useState({});

  const columnVisibilityModel = useMemo(() => {
    const model = {};
    for (const col of gridDisplayColumns) {
      const f = col.field;
      model[f] = Object.prototype.hasOwnProperty.call(columnVisibilityModelState, f)
        ? columnVisibilityModelState[f]
        : (f === '__loading__' || DATA_ANALYSIS_DEFAULT_VISIBLE_FIELDS.has(f));
    }
    return model;
  }, [gridDisplayColumns, columnVisibilityModelState]);

  const handleColumnVisibilityModelChange = useCallback((model) => {
    // The grid hydrates columns during its own render and reports the visibility model from that pass.
    queueMicrotask(() => {
      setColumnVisibilityModelState((current) => {
        const fields = Object.keys(model).filter((field) => !field.startsWith('__'));
        if (fields.every((field) => current[field] === model[field])) return current;
        const next = { ...current };
        fields.forEach((field) => {
          next[field] = model[field];
        });
        return next;
      });
    });
  }, []);

  const handleFilterModelChange = useCallback((model) => {
    // The loading placeholder has no Client column. The grid drops that filter during render.
    if (!hasDataColumns) return;
    queueMicrotask(() => setFilterModel(model));
  }, [hasDataColumns]);

  const isGroupedView = negotiationObj?.MuniCode === 'All' || negotiationObj?.MuniCode?.length === 1;

  const descriptionCounts = useMemo(() => {
    if (isGroupedView) {
      // Group by municipality for county runs or by first character for "All"
      const municipalCounts = {};
      const totalUniqueEntries = new Set();
      // Ensure rows is an array before filtering
      const rowsArray = Array.isArray(rows) ? rows : [];
      rowsArray.filter(row => {
        if (!filterModel?.items?.length) return true;
        return filterModel.items.every(filter => {
          if (filter.field === 'Description') return true;
          if (filter.field === 'Client') return row.Client === 1 || row.Client === '1';
          if (filter.field === 'IsSalesComp') return row.IsSalesComp === 1 || row.IsSalesComp === '1';
          if (filter.field === 'IsAssessmentComp') return row.IsAssessmentComp === 1 || row.IsAssessmentComp === '1';
          return true;
        });
      }).forEach(row => {
        const description = row.Description || 'No Description';
        const municipality = negotiationObj?.MuniCode === 'All'
          ? row.parcel_id?.substring(0, 1) || 'Unknown'
          : row.parcel_id?.substring(0, 3) || 'Unknown';
        if (!municipalCounts[municipality]) {
          municipalCounts[municipality] = {
            total: 0,
            items: {},
            uniqueEntries: new Set()
          };
        }
        const matches = description.match(/\((.*?)\)/g) || [];
        if (matches.length > 0) {
          municipalCounts[municipality].uniqueEntries.add(row.parcel_id);
          totalUniqueEntries.add(row.parcel_id);
        }
        matches.forEach(match => {
          const content = match.slice(1, -1);
          if (content.endsWith('_EXTREME')) {
            const field = content.replace('_EXTREME', '');
            const desc = `Extreme ${field}`;
            municipalCounts[municipality].items[desc] = (municipalCounts[municipality].items[desc] || 0) + 1;
          } else if (content.endsWith('_NA')) {
            const field = content.replace('_NA', '');
            const desc = `Missing ${field === 'PhotoURL' ? 'Photo' : field}`;
            municipalCounts[municipality].items[desc] = (municipalCounts[municipality].items[desc] || 0) + 1;
          }
        });
      });
      
      // Sort municipalities numerically (S01, S02, etc.) instead of by count
      const result = Object.entries(municipalCounts)
        .map(([municipality, data]) => ({
          municipality,
          total: data.uniqueEntries.size.toLocaleString(),
          totalCount: data.uniqueEntries.size, // Keep raw count for percentage calculations
          items: Object.entries(data.items).map(([description, count]) => ({
            description,
            count: count.toLocaleString(),
            rawCount: count // Keep raw count for percentage calculations
          })).sort((a, b) => b.rawCount - a.rawCount)
        }))
        .sort((a, b) => {
          // Extract numeric part from municipality code for proper sorting
          const aNum = parseInt(a.municipality.replace(/\D/g, '')) || 0;
          const bNum = parseInt(b.municipality.replace(/\D/g, '')) || 0;
          return aNum - bNum; // Sort numerically ascending
        });
      
      
      const totalSum = result.reduce((sum, row) => {
        return sum + row.totalCount;
      }, 0);
      
      if (!selectedDescription) {
        result.unshift({
          description: 'Total',
          total: totalSum.toLocaleString()
        });
      }
      return result;
    } else {
      // Regular counting for non-county runs (lowest/municipal level)
      const uniqueEntries = new Set();
      const itemCounts = {};
      rows.filter(row => {
        if (!filterModel?.items?.length) return true;
        return filterModel.items.every(filter => {
          if (filter.field === 'Description') return true;
          if (filter.field === 'Client') return row.Client === 1 || row.Client === '1';
          if (filter.field === 'IsSalesComp') return row.IsSalesComp === 1 || row.IsSalesComp === '1';
          if (filter.field === 'IsAssessmentComp') return row.IsAssessmentComp === 1 || row.IsAssessmentComp === '1';
          return true;
        });
      }).forEach(row => {
        const description = row.Description || 'No Description';
        const matches = description.match(/\((.*?)\)/g) || [];
        if (matches.length > 0) {
          uniqueEntries.add(row.parcel_id);
        }
        matches.forEach(match => {
          const content = match.slice(1, -1);
          if (content.endsWith('_EXTREME')) {
            const field = content.replace('_EXTREME', '');
            const desc = `Extreme ${field}`;
            itemCounts[desc] = (itemCounts[desc] || 0) + 1;
          } else if (content.endsWith('_NA')) {
            const field = content.replace('_NA', '');
            const desc = `Missing ${field === 'PhotoURL' ? 'Photo' : field}`;
            itemCounts[desc] = (itemCounts[desc] || 0) + 1;
          }
        });
      });
      return Object.entries(itemCounts)
        .map(([description, count]) => ({
          description,
          count: count.toLocaleString()
        }))
        .sort((a, b) => parseInt(b.count.replace(/,/g, '')) - parseInt(a.count.replace(/,/g, '')));
    }
  }, [rows, filterModel, negotiationObj?.MuniCode, isGroupedView, selectedDescription]);

  // Memoize the summary stats handlers
  const handleSummaryRowClick = useCallback((filter) => {
    let description, municipality;
    if (typeof filter === 'object') {
      description = filter.description;
      municipality = filter.municipality;
    } else {
      description = filter;
      municipality = null;
    }
    
    logger.log('=== handleSummaryRowClick DEBUG ===');
    logger.log('Input filter:', filter);
    logger.log('Parsed description:', description);
    logger.log('Parsed municipality:', municipality);
    logger.log('Current selectedDescription:', selectedDescription);
    logger.log('Current filterModel:', filterModel);
    
    const newFilterItems = [...filterModel.items];
    // Remove any existing Description filters
    const filteredItems = newFilterItems.filter(item => item.field !== 'Description' && item.field !== 'parcel_id');
    logger.log('Filtered items after removing Description/parcel_id:', filteredItems);
    
    // Toggle selection
    if (selectedDescription === description) {
      logger.log('Clearing filter - same description clicked');
      setSelectedDescription(null);
    } else {
      logger.log('Setting new filter for description:', description);
      setSelectedDescription(description);
      // Extract the field name from the description
      let fieldName;
      if (description.startsWith('Missing ')) {
        fieldName = description.replace('Missing ', '');
        if (fieldName === 'Photo') fieldName = 'PhotoURL';
        // Add new Description filter using the field name - only for missing values
        const newFilter = {
          field: 'Description',
          operator: 'contains',
          value: `(${fieldName}_NA)`
        };
        filteredItems.push(newFilter);
        logger.log('Added Missing filter:', newFilter);
      } else if (description.startsWith('Extreme ')) {
        fieldName = description.replace('Extreme ', '');
        // Add new Description filter using the field name - only for extreme values
        const newFilter = {
          field: 'Description',
          operator: 'contains',
          value: `(${fieldName}_EXTREME)`
        };
        filteredItems.push(newFilter);
        logger.log('Added Extreme filter:', newFilter);
      }
      // If in grouped view and municipality is set, add a filter for that municipality
      if (isGroupedView && municipality) {
        const muniFilter = {
          field: 'parcel_id',
          operator: 'startsWith',
          value: municipality
        };
        filteredItems.push(muniFilter);
        logger.log('Added municipality filter:', muniFilter);
      } else {
        logger.log('No municipality filter added (municipality:', municipality, ', isGroupedView:', isGroupedView, ')');
      }
    }
    
    const newFilterModel = {
      items: filteredItems,
      logicOperator: 'and'
    };
    
    logger.log('Final newFilterModel:', newFilterModel);
    logger.log('=== END handleSummaryRowClick DEBUG ===');
    
    setFilterModel(newFilterModel);
  }, [filterModel, selectedDescription, isGroupedView]);

  // Memoize the summary stats props
  const summaryStatsProps = useMemo(() => ({
    descriptionCounts,
    selectedDescription,
    onRowClick: handleSummaryRowClick,
    isCountyRun: negotiationObj?.MuniCode?.length === 1,
    isGroupedView,
    totalRows,
    clientCount,
    activeTab  // Add activeTab to props
  }), [
    descriptionCounts,
    selectedDescription,
    handleSummaryRowClick,
    negotiationObj?.MuniCode,
    isGroupedView,
    totalRows,
    clientCount,
    activeTab  // Add to dependencies
  ]);

  // Handle tab change
  const handleTabChange = useCallback((_, newValue) => {
    setActiveTab(newValue);
    let newFilterItems = [...filterModel.items];
    
    // Remove any existing Client/Comp filters
    newFilterItems = newFilterItems.filter(item => 
      item.field !== 'Client' && 
      item.field !== 'IsSalesComp' && 
      item.field !== 'IsAssessmentComp'
    );
    
    // Add new filter based on selected tab
    if (newValue === 'clients') {
      newFilterItems.push({ id: 'tab-client', field: 'Client', operator: '=', value: '1' });
    } else if (newValue === 'salesComps') {
      newFilterItems.push({ id: 'tab-sales', field: 'IsSalesComp', operator: '=', value: '1' });
    } else if (newValue === 'equityComps') {
      newFilterItems.push({ id: 'tab-equity', field: 'IsAssessmentComp', operator: '=', value: '1' });
    }
    
    setFilterModel({
      items: newFilterItems,
      logicOperator: 'and'
    });
  }, [filterModel]);

  // Determine if the update button should be enabled
  const updateEnabled = useMemo(() => {
    if (hasEdits) return true;
    // Check if there are ANY flags selected (either data or outliers)
    const hasDataFlags = badDataFlags.data && badDataFlags.data.length > 0;
    const hasOutlierFlags = badDataFlags.outliers && badDataFlags.outliers.length > 0;
    return hasDataFlags || hasOutlierFlags;
  }, [badDataFlags, hasEdits]);

  // Handler for update button
  const handleUpdateData = async () => {
    // new function to actually update
    await flagBadDataAndUpdateReport();
    // here - for the individual edit
  };

  // Update the getCellClassName function to handle photo updates
  const getCellClassName = useCallback((params) => {
    const row = params.row;
    const fieldName = params.field;
    const description = row.Description || '';

    // Mark as edited if there *is* an entry for this field, even if it's 0
    const unsavedRow = unsavedChangesRef.current.unsavedRows[row.parcel_id];
    const isEdited = unsavedRow != null && Object.prototype.hasOwnProperty.call(unsavedRow, fieldName);

    if (isEdited) return 'fixed-data-cell';

    if (isReturnAllBadData) {
      return '';
    }

    const isBad = hasFieldIssue(description, fieldName);
    if (isBad) return 'missing-data-cell';
    return '';
  }, [isReturnAllBadData, hasFieldIssue]);

  const handleCellEditCommit = useCallback((params) => {
    try {
      const { id, field, value } = params;
      const row = rows.find(r => r.id === id);
      if (!row) return;

      // Store the original value if not already stored
      if (!unsavedChangesRef.current.rowsBeforeChange[row.parcel_id]) {
        unsavedChangesRef.current.rowsBeforeChange[row.parcel_id] = { ...row };
      }

      // Update the unsaved changes
      if (!unsavedChangesRef.current.unsavedRows[row.parcel_id]) {
        unsavedChangesRef.current.unsavedRows[row.parcel_id] = {};
      }
      unsavedChangesRef.current.unsavedRows[row.parcel_id][field] = value;

      // Force a re-render of the cell
      apiRef.current.updateRows([{ id, [field]: value }]);

      // Set hasEdits to true when a cell is edited
      setHasEdits(true);
    } catch (error) {
      logger.error('Error in handleCellEditCommit:', error);
    }
  }, [rows]);

  const startCellEdit = useCallback((params) => {
    const cellMode = apiRef.current.getCellMode(params.id, params.field);
    if (cellMode === 'view') {
      apiRef.current.startCellEditMode({ id: params.id, field: params.field });
    }
  }, [apiRef]);

  const hasExistingCellValue = useCallback((value) => {
    // Treat null/undefined/empty-string as "no existing data" (but keep 0/false as existing values)
    return value !== null && value !== undefined && value !== '';
  }, []);



  // Track fields we have force-enabled editing on after user confirmation
  const overrideEditFieldsRef = useRef(new Set());

  // Modal state for edit confirmation
  const [confirmEditOpen, setConfirmEditOpen] = useState(false);
  const pendingCellRef = useRef(null);

  const handleCellDoubleClick = useCallback((params, event) => {
    // Prevent default (which would attempt to enter edit directly)
    event.defaultMuiPrevented = true;

    // If there is no existing value, allow normal edit (no confirmation / no double-click guard)
    if (!hasExistingCellValue(params.value)) {
      if (isCellEditable(params)) startCellEdit(params);
      return;
    }

    // Check if cell is already being edited
    const cellMode = apiRef.current.getCellMode(params.id, params.field);
    if (cellMode === 'edit') {
      return; // Don't show confirmation if already editing
    }

    // Check if the cell is actually editable before showing confirmation
    if (!isCellEditable(params)) {
      logger.warn(`Cell ${params.id}-${params.field} is not editable, skipping confirmation dialog`);
      return; // Don't show confirmation for non-editable cells
    }

    pendingCellRef.current = params;
    setConfirmEditOpen(true);
  }, [apiRef, hasExistingCellValue, isCellEditable, startCellEdit]);

  const confirmEdit = useCallback(() => {
    const params = pendingCellRef.current;
    if (params) {
      // Check if the cell is actually editable before proceeding
      if (isCellEditable(params)) {
        overrideEditFieldsRef.current.add(`${params.id}-${params.field}`);
        startCellEdit(params);
      } else {
        // If not editable, just close the dialog without attempting to edit
        logger.warn(`Cell ${params.id}-${params.field} is not editable, skipping edit mode`);
      }
    }
    setConfirmEditOpen(false);
  }, [startCellEdit, isCellEditable]);

  const cancelEdit = useCallback(() => {
    setConfirmEditOpen(false);
  }, []);

  // Handler for removing rows when data is not available
  const handleRemoveRowForDataNotAvailable = useCallback((parcelId) => {
    setRemovedRows(prev => new Set([...prev, parcelId]));
  }, []);

  // Unified handler for "Data not available" - works for both single and bulk operations
  const handleDataNotAvailable = useCallback(async (rows, handleClose = null) => {
    // Normalize input - convert single row to array for consistent processing
    const rowsArray = Array.isArray(rows) ? rows : [rows];
    const parcelIds = rowsArray.map(row => row.parcel_id);
    const isBulk = rowsArray.length > 1;
    
    try {
      setIsFlaggingData(true);
      
      logger.log(`${isBulk ? 'Bulk' : 'Single'} data not available for parcels:`, parcelIds);
      
      // Save ALL unsaved changes before removing the rows
      const allUnsavedChanges = unsavedChangesRef.current.unsavedRows;
      logger.log('Checking for unsaved changes across all parcels');
      logger.log('All unsaved changes:', allUnsavedChanges);
      
      if (allUnsavedChanges && Object.keys(allUnsavedChanges).length > 0) {
        logger.log(`Saving all unsaved changes before ${isBulk ? 'bulk' : ''} marking as data not available:`, allUnsavedChanges);
        
        try {
          // Save ALL changes to the database
          await updateDatabaseNewFunction(allUnsavedChanges, user?.userId);
          
          logger.log(`All unsaved changes saved successfully before ${isBulk ? 'bulk' : ''} marking as data not available`);
          toast.success('All changes saved before marking as data not available');
          
          // Clear ALL unsaved changes
          unsavedChangesRef.current.unsavedRows = {};
          unsavedChangesRef.current.rowsBeforeChange = {};
          
          // Refresh the data grid to show the updated data
          logger.log('Refreshing data grid with updated data...');
          const params = {
            MuniCode: negotiationObj?.MuniCode || 'All',
            TaxYear: rowsArray[0]?.TaxYear || negotiationObj?.TaxYear,
            county: negotiationObj?.county,
            municipality: negotiationObj?.municipality,
            village: negotiationObj?.village,
            villageRun: negotiationObj?.villageRun || 0,
            UserId: user?.userId || null,
            TrainingUser: user?.userAccess?.level === 'Training' ? 1 : 0,
            reportType: negotiationObj?.reportType || 'data_analysis',
            BadDataFields: badDataFlags?.data || [],
            OutlierFields: outlierFieldsForBadDataRequest(badDataFlags),
            CourtDate: negotiationObj?.CourtDate || null,
            CourtRun: negotiationObj?.CourtRun || 0,
            runType: negotiationObj?.runType,
            uids: negotiationObj?.uids,
            overrideIds: negotiationObj?.overrideIds,
          };
          
          await fetchBadDataReport(params, {
            initialInvocation: false,
            setDataAnalysisResult,
            setDataAnalysisColumns,
            setDataAnalysisFilterModel,
            filterModel
          });
          
          logger.log('Data grid refreshed with updated data');
        } catch (saveError) {
          logger.error(`Failed to save unsaved changes before ${isBulk ? 'bulk' : ''} marking as data not available:`, saveError);
          // Don't proceed with marking as data not available if save fails
          return;
        }
      }
      
      // Complete DataCleaning tasks
      await completeCaseStatus(parcelIds, 'DataCleaning', {
        muniCode: negotiationObj?.MuniCode || 'All',
        taxYear: rowsArray[0]?.TaxYear || negotiationObj?.TaxYear,
        userId: user?.userId || null,
        userAccessLevel: user?.userAccess?.level || null,
        completeAllCaseTypes: true,
      });

      logger.log(`${isBulk ? 'Bulk' : 'Single'} tasks updated successfully for data not available`);
      toast.success(`${parcelIds.length} task${parcelIds.length > 1 ? 's' : ''} marked as completed - data not available`);
      
      // Remove rows from the data grid
      if (isBulk) {
        setRemovedRows(prev => new Set([...prev, ...parcelIds]));
        setSelectionModel(createEmptyRowSelection()); // Clear selection for bulk operations
      } else {
        handleRemoveRowForDataNotAvailable(parcelIds[0]);
      }
      
    } catch (error) {
      logger.error(`Error updating ${isBulk ? 'bulk' : ''} tasks for data not available:`, error);
    } finally {
      setIsFlaggingData(false);
    }
    
    // Call handleClose if provided (for single row operations)
    if (handleClose) {
      handleClose();
    }
  }, [negotiationObj?.MuniCode, negotiationObj?.TaxYear, user?.userId, handleRemoveRowForDataNotAvailable, badDataFlags, memoizedColumns, setDataAnalysisResult, setDataAnalysisColumns, setDataAnalysisFilterModel, filterModel]);

  // Function to process the property info request
  const processPropertyInfoRequest = useCallback(async (row, handleClose) => {
    try {
      setIsFlaggingData(true);
      
      // Complete DataCleaning task
      await completeCaseStatus([row.parcel_id], 'DataCleaning', {
        muniCode: negotiationObj?.MuniCode || 'All',
        taxYear: row.TaxYear,
        userId: user?.userId || null,
        userAccessLevel: user?.userAccess?.level || null,
        completeAllCaseTypes: true,
      });

      logger.log('Task updated successfully');

      // Call the property inventory request API
      const inventoryRequestData = {
        parcel_id: row.parcel_id,
        tax_year: row.TaxYear,
        UserId: user?.userId || null
      };
      
      // THIS ROUTE NEEDS TO BE UPDATED WITH FUNCTIONALITY ONCE TEMPLATE EXISTS
      await axiosInstance.post('/request_property_inventory', inventoryRequestData);
      logger.log('Property inventory request submitted successfully');
      toast.success('Property inventory request submitted');
      
      // Remove this row from the data grid by updating the parent component's state
      handleRemoveRowForDataNotAvailable(row.parcel_id);
      
    } catch (error) {
      logger.error('Error with property info request:', error);
    } finally {
      setIsFlaggingData(false);
    }
    
    handleClose();
  }, [negotiationObj?.MuniCode, user?.userId, handleRemoveRowForDataNotAvailable]);

  // Property info request confirmation handlers
  const handlePropertyInfoConfirm = useCallback(async () => {
    if (pendingPropertyInfoRequest) {
      await processPropertyInfoRequest(pendingPropertyInfoRequest.row, pendingPropertyInfoRequest.handleClose);
    }
    setPropertyInfoConfirmOpen(false);
    setPendingPropertyInfoRequest(null);
  }, [pendingPropertyInfoRequest, processPropertyInfoRequest]);

  const handlePropertyInfoCancel = useCallback(() => {
    setPropertyInfoConfirmOpen(false);
    setPendingPropertyInfoRequest(null);
  }, []);

  // Handler for showing property info confirmation dialog
  const handleShowPropertyInfoConfirm = useCallback(async (row, handleClose) => {
    setLoadingTaskDetails(true);
    setPropertyInfoConfirmOpen(true);
    
    // Get additional task details for the confirmation dialog
    try {
      const taskStatusData = {
        parcel_id: row.parcel_id,
        review_type: 'DataCleaning',
        year: row.TaxYear
      };
      
      const response = await axiosInstance.post('/check_task_status', taskStatusData);
      
      if (response.data.success && response.data.task_data) {
        setPendingPropertyInfoRequest({ 
          row, 
          handleClose, 
          taskData: response.data.task_data 
        });
      } else {
        setPendingPropertyInfoRequest({ row, handleClose });
      }
    } catch (error) {
      logger.error('Error getting task details:', error);
      setPendingPropertyInfoRequest({ row, handleClose });
    } finally {
      setLoadingTaskDetails(false);
    }
  }, []);

  // Handler for processing property info request
  const handleProcessPropertyInfoRequest = useCallback(async (row, handleClose) => {
    await processPropertyInfoRequest(row, handleClose);
  }, [processPropertyInfoRequest]);

  // Handle bulk menu click
  const handleBulkMenuClick = useCallback((event) => {
    setBulkMenuAnchorEl(event.currentTarget);
  }, []);

  const handleBulkMenuClose = useCallback(() => {
    setBulkMenuAnchorEl(null);
  }, []);

  // Flag bad sale handlers
  const handleBulkFlagBadSale = useCallback(() => {
    setFlagBadSaleDialogOpen(true);
    setBulkMenuAnchorEl(null);
  }, []);

  const handleFlagBadSaleCancel = useCallback(() => {
    setFlagBadSaleDialogOpen(false);
  }, []);
  // Had to remove the rows out of the general datagrid props bc it kept passing a new reference and causing rerenders.
  const memoizedRows = useMemo(() => {
    const filteredRows = rows
      .filter(row => !removedRows.has(row.parcel_id))
      .map(row => ({
        ...row,
        id: row.id || row.parcel_id,
        TaxYear: negotiationObj?.TaxYear, // Add TaxYear from negotiationObj
      }));
    return filteredRows;
  }, [rows, removedRows, negotiationObj?.TaxYear]);

  // Flag bad sale confirm handler (defined after memoizedRows)
  const handleFlagBadSaleConfirm = useCallback(async ({ SaleIssue, Memo }) => {
    if (selectedRowIds.length === 0) return;

    try {
      setIsFlaggingBadSale(true);
      
      // Get selected rows and prepare sales data
      const selectedRows = memoizedRows.filter(row => selectedRowIds.includes(row.id));
      const salesData = prepareSalesData(selectedRows, { SaleIssue, Memo, UserID: user?.userId });
      
      // Flag all selected sales using the helper
      await flagMultipleBadSales(salesData);
      
      // Refresh the data grid
      const params = {
        MuniCode: negotiationObj?.MuniCode || 'All',
        TaxYear: negotiationObj?.TaxYear,
        county: negotiationObj?.county,
        municipality: negotiationObj?.municipality,
        village: negotiationObj?.village,
        villageRun: negotiationObj?.villageRun || 0,
        UserId: user?.userId || null,
        TrainingUser: user?.userAccess?.level === 'Training' ? 1 : 0,
        reportType: negotiationObj?.reportType || 'data_analysis',
        BadDataFields: badDataFlags.data,
        OutlierFields: outlierFieldsForBadDataRequest(badDataFlags),
        CourtDate: negotiationObj?.CourtDate || null,
        CourtRun: negotiationObj?.CourtRun || 0,
        runType: negotiationObj?.runType,
        uids: negotiationObj?.uids,
        overrideIds: negotiationObj?.overrideIds,
      };
      
      await fetchBadDataReport(params, {
        initialInvocation: false,
        setDataAnalysisResult,
        setDataAnalysisColumns,
        setDataAnalysisFilterModel,
        filterModel
      });
    } catch (error) {
      logger.error('Error flagging bad sales:', error);
    } finally {
      setIsFlaggingBadSale(false);
      setFlagBadSaleDialogOpen(false);
    }
  }, [selectionModel, memoizedRows, negotiationObj, user, badDataFlags, setDataAnalysisResult, setDataAnalysisColumns, setDataAnalysisFilterModel, filterModel]);

  // Bulk operation handler - wrapper for unified function
  const handleBulkDataNotAvailable = useCallback(async () => {
    if (selectedRowIds.length === 0) return;
    
    // Get the selected rows data
    const selectedRows = memoizedRows.filter(row => selectedRowIds.includes(row.id));
    
    // Call the unified function with the selected rows
    await handleDataNotAvailable(selectedRows);
    
    // Close the bulk menu
    setBulkMenuAnchorEl(null);
  }, [selectionModel, memoizedRows, handleDataNotAvailable]);

  const handleBulkRequestInventory = useCallback(async () => {
    if (selectedRowIds.length === 0) return;

    try {
      setIsFlaggingData(true);
      
      // Get the selected rows data
      const selectedRows = memoizedRows.filter(row => selectedRowIds.includes(row.id));
      const parcelIds = selectedRows.map(row => row.parcel_id);
      
      logger.log('Bulk request inventory for parcels:', parcelIds);
      
      // Save ALL unsaved changes before removing the rows
      const allUnsavedChanges = unsavedChangesRef.current.unsavedRows;
      logger.log('Checking for unsaved changes across all parcels before bulk inventory request');
      logger.log('All unsaved changes:', allUnsavedChanges);
      
      if (allUnsavedChanges && Object.keys(allUnsavedChanges).length > 0) {
        logger.log('Saving all unsaved changes before bulk inventory request:', allUnsavedChanges);
        
        try {
          // Save ALL changes to the database
          await updateDatabaseNewFunction(allUnsavedChanges, user?.userId);
          
          logger.log('All unsaved changes saved successfully before bulk inventory request');
          toast.success('All changes saved before inventory request');
          
          // Clear ALL unsaved changes
          unsavedChangesRef.current.unsavedRows = {};
          unsavedChangesRef.current.rowsBeforeChange = {};
          
          // Refresh the data grid to show the updated data
          logger.log('Refreshing data grid with updated data...');
          const params = {
            MuniCode: negotiationObj?.MuniCode || 'All',
            TaxYear: selectedRows[0]?.TaxYear || negotiationObj?.TaxYear,
            county: negotiationObj?.county,
            municipality: negotiationObj?.municipality,
            village: negotiationObj?.village,
            villageRun: negotiationObj?.villageRun || 0,
            UserId: user?.userId || null,
            TrainingUser: user?.userAccess?.level === 'Training' ? 1 : 0,
            reportType: negotiationObj?.reportType || 'data_analysis',
            BadDataFields: badDataFlags?.data || [],
            OutlierFields: outlierFieldsForBadDataRequest(badDataFlags),
            CourtDate: negotiationObj?.CourtDate || null,
            CourtRun: negotiationObj?.CourtRun || 0,
            runType: negotiationObj?.runType,
            uids: negotiationObj?.uids,
            overrideIds: negotiationObj?.overrideIds,
          };
          
          await fetchBadDataReport(params, {
            initialInvocation: false,
            setDataAnalysisResult,
            setDataAnalysisColumns,
            setDataAnalysisFilterModel,
            filterModel
          });
          
          logger.log('Data grid refreshed with updated data');
        } catch (saveError) {
          logger.error('Failed to save unsaved changes before bulk inventory request:', saveError);
          // Don't proceed with inventory request if save fails
          return;
        }
      }
      
      // Complete DataCleaning tasks
      await completeCaseStatus(parcelIds, 'DataCleaning', {
        muniCode: negotiationObj?.MuniCode || 'All',
        taxYear: selectedRows[0]?.TaxYear || negotiationObj?.TaxYear,
        userId: user?.userId || null,
        userAccessLevel: user?.userAccess?.level || null,
        completeAllCaseTypes: true,
      });

      // Call the property inventory request API for each parcel
      const inventoryPromises = selectedRows.map(row => {
        const inventoryRequestData = {
          parcel_id: row.parcel_id,
          tax_year: row.TaxYear,
          UserId: user?.userId || null
        };
        
        // THIS ROUTE NEEDS TO BE UPDATED WITH FUNCTIONALITY ONCE TEMPLATE EXISTS
        return axiosInstance.post('/request_property_inventory', inventoryRequestData);
      });
      
      await Promise.all(inventoryPromises);
      logger.log('Bulk property inventory requests submitted successfully');
      toast.success(`${parcelIds.length} property inventory requests submitted`);
      
      // Remove these rows from the data grid
      setRemovedRows(prev => new Set([...prev, ...parcelIds]));
      
      // Clear selection
      setSelectionModel(createEmptyRowSelection());
      
    } catch (error) {
      logger.error('Error with bulk inventory requests:', error);
    } finally {
      setIsFlaggingData(false);
    }
    
    setBulkMenuAnchorEl(null);
  }, [selectionModel, memoizedRows, negotiationObj?.MuniCode, negotiationObj?.TaxYear, user?.userId]);









  // Handle photo updates from the PhotoCell component
  const handlePhotoUpdate = useCallback((newUrl, parcelId) => {
    // Find the row with this parcel ID
    const row = memoizedRows.find(r => r.parcel_id === parcelId);
    if (!row) return;

    const rowId = row.id || parcelId;

    // Update the cell with the new photo URL using the same pattern as other field edits
    handleCellEditCommit({
      id: rowId,
      field: 'PhotoURL',
      value: newUrl
    });

    // Force a re-render of the cell
    apiRef.current.updateRows([{
      id: rowId,
      PhotoURL: newUrl
    }]);
  }, [memoizedRows, handleCellEditCommit, apiRef]);

  // Simplified renderPhotoCell using the new PhotoCell component
  const renderPhotoCell = useCallback((params) => {
    return (
      <div style={{ 
        width: '100%', 
        height: '100%', 
        padding: '0px !important', 
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <PhotoCell
          key={params.row.parcel_id}
          parcelId={params.row.parcel_id}
          MLSPhotoFolder={params.row.MLSPhotoFolder}
          onPhotoUpdate={handlePhotoUpdate}
          cellMode="edit"
          showUploadPrompt={true}
          style={{ width: '100%', height: '100%' }}
        />
      </div>
    );
  }, [handlePhotoUpdate]);

  // Add this helper function to find the next editable cell
  const findNextEditableCell = useCallback((currentRowIndex, currentColIndex, direction = 'forward') => {
    const visibleColumns = memoizedColumns.filter(col => 
      columnVisibilityModel[col.field]
    );
    
    // Define which fields are actually editable (have data issues or are manually enabled)
    // PCC is explicitly excluded from editable fields
    const editableFields = EDITABLE_FIELDS;
    
    if (direction === 'forward') {
      // First try to find next editable cell in current row
      for (let i = currentColIndex + 1; i < visibleColumns.length; i++) {
        const col = visibleColumns[i];
        const row = memoizedRows[currentRowIndex];
        if (row && editableFields.includes(col.field)) {
          const description = row.Description || '';
          const fieldName = col.field;
          const isOverride = overrideEditFieldsRef.current.has(`${row.id}-${fieldName}`);
          if ((isReturnAllBadData ? isOverride : (hasFieldIssue(description, fieldName) || isOverride))) {
            return { rowIndex: currentRowIndex, colIndex: i };
          }
        }
      }
      // If no editable cell found in current row, move to next row
      for (let rowIndex = currentRowIndex + 1; rowIndex < memoizedRows.length; rowIndex++) {
        const row = memoizedRows[rowIndex];
        for (let colIndex = 0; colIndex < visibleColumns.length; colIndex++) {
          const col = visibleColumns[colIndex];
          if (editableFields.includes(col.field)) {
            const description = row.Description || '';
            const fieldName = col.field;
            const isOverride = overrideEditFieldsRef.current.has(`${row.id}-${fieldName}`);
            if ((isReturnAllBadData ? isOverride : (hasFieldIssue(description, fieldName) || isOverride))) {
              return { rowIndex, colIndex };
            }
          }
        }
      }
    } else {
      // Backward navigation
      // First try to find previous editable cell in current row
      for (let i = currentColIndex - 1; i >= 0; i--) {
        const col = visibleColumns[i];
        const row = memoizedRows[currentRowIndex];
        if (row && editableFields.includes(col.field)) {
          const description = row.Description || '';
          const fieldName = col.field;
          const isOverride = overrideEditFieldsRef.current.has(`${row.id}-${fieldName}`);
          if ((isReturnAllBadData ? isOverride : (hasFieldIssue(description, fieldName) || isOverride))) {
            return { rowIndex: currentRowIndex, colIndex: i };
          }
        }
      }
      // If no editable cell found in current row, move to previous row
      for (let rowIndex = currentRowIndex - 1; rowIndex >= 0; rowIndex--) {
        const row = memoizedRows[rowIndex];
        for (let colIndex = visibleColumns.length - 1; colIndex >= 0; colIndex--) {
          const col = visibleColumns[colIndex];
          if (editableFields.includes(col.field)) {
            const description = row.Description || '';
            const fieldName = col.field;
            const isOverride = overrideEditFieldsRef.current.has(`${row.id}-${fieldName}`);
            if ((isReturnAllBadData ? isOverride : (hasFieldIssue(description, fieldName) || isOverride))) {
              return { rowIndex, colIndex };
            }
          }
        }
      }
    }
    return null;
  }, [memoizedColumns, columnVisibilityModel, memoizedRows, hasFieldIssue, isReturnAllBadData]);

  // Add this function to handle tab navigation
  const handleTabNavigation = useCallback((params, event) => {
    const { id, field } = params;
    const currentRowIndex = memoizedRows.findIndex(row => row.id === id);
    const visibleColumns = memoizedColumns.filter(col => 
      columnVisibilityModel[col.field]
    );
    const currentColIndex = visibleColumns.findIndex(col => col.field === field);
    
    if (currentRowIndex === -1 || currentColIndex === -1) return;
    
    const direction = event.shiftKey ? 'backward' : 'forward';
    const nextCell = findNextEditableCell(currentRowIndex, currentColIndex, direction);
    
    if (nextCell) {
      event.preventDefault();

      const { id: nextId } = memoizedRows[nextCell.rowIndex];
      const nextField = visibleColumns[nextCell.colIndex].field;

      // 1) Move the grid's internal focus
      apiRef.current.setCellFocus(nextId, nextField);

      // 2) Ensure the cell is in view mode before starting edit mode
      setTimeout(() => {
        // Check if the cell is in view mode before starting edit
        const cellMode = apiRef.current.getCellMode(nextId, nextField);
        if (cellMode === 'view') {
          apiRef.current.startCellEditMode({ id: nextId, field: nextField });
        }
      }, 50); // Increased timeout to ensure focus is set
    }
  }, [findNextEditableCell, memoizedRows, memoizedColumns, columnVisibilityModel, apiRef, hasFieldIssue]);

  const parcelKind = { clients: 'client', salesComps: 'sale', equityComps: 'equity' }[activeTab];
  const noMissingDataLabel = parcelKind
    ? `No ${parcelKind} parcels missing data.`
    : 'No parcels missing data.';
  const analysisHasRun = hasDataColumns || initialFilterModel != null;

  // Memoize the DataGrid component props
  const dataGridProps = useMemo(() => {
    return {
    columns: gridDisplayColumns.map(col => {
      if (col.field === '__loading__') {
        return col;
      }

      let colType;
      if (numericEditableFields.includes(col.field)) {
        colType = 'number';
      } else if (booleanLikeFields.includes(col.field)) {
        colType = 'number'; // because we use 1/0 in filters
      }

      const finalType = colType || col.type;
      
      return {
        ...col,
        // columnDefinitions sets cellClassName per field from Description; suppress when Return All
        ...(isReturnAllBadData && col.field !== 'PhotoURL' ? { cellClassName: () => '' } : {}),
        editable: true,
        type: finalType, // Use colType if set, otherwise use the type from columnDefinitions
        // Custom sorting to put blank values at the end
        sortComparator: (v1, v2) => {
          // Handle blank/null values - put them at the end
          const isV1Blank = v1 === null || v1 === undefined || v1 === '';
          const isV2Blank = v2 === null || v2 === undefined || v2 === '';
          
          if (isV1Blank && isV2Blank) return 0;
          if (isV1Blank) return 1;  // v1 is blank, put it after v2
          if (isV2Blank) return -1; // v2 is blank, put it after v1
          
          // Both values are not blank, use appropriate comparison
          if (finalType === 'number') {
            return Number(v1) - Number(v2);
          }
          return String(v1).localeCompare(String(v2));
        },
        // Ensure Description field is filterable
        ...(col.field === 'Description' && {
          filterable: true
        }),
        // Add custom editor for Style field
        ...(col.field === 'Style' && {
          renderEditCell: (params) => <StyleSelectEditor {...params} />
        }),
        // Add custom editor for Pool field
        ...(col.field === 'InGroundPool' && {
          renderEditCell: (params) => <PoolSelectEditor {...params} />
        }),
        // Add custom value formatter for IFMVRatio to display as percentage
        ...(col.field === 'IFMVRatio' && {
          valueFormatter: (value) => {
            if (value === null || value === undefined || value === '') return '';
            const numValue = typeof value === 'number' ? value : parseFloat(value);
            if (isNaN(numValue)) return '';
            return `${(numValue * 100).toFixed(2)}%`;
          }
        }),
        // Remove padding for photo cells
        ...(col.field === 'PhotoURL' && {
          cellClassName: 'photo-cell-no-padding',
          disableClickEventBubbling: true,
        }),
        renderCell: (params) => {
          if (params.field === 'Description' && isReturnAllBadData) {
            return 'All data';
          }
          // For fields with custom valueFormatters, call the formatter and return the formatted value
          if (col.valueFormatter) {
            return col.valueFormatter(params.value);
          }
          
          if (params.field === 'Address') {
            return <AddressCell 
              row={params.row} 
              value={params.value} 
              onRequestPropertyInfo={handleProcessPropertyInfoRequest}
              onShowPropertyInfoConfirm={handleShowPropertyInfoConfirm}
              onDataNotAvailable={handleDataNotAvailable}
            />;
          }
          if (params.field === 'PhotoURL') {
            return renderPhotoCell(params);
          }
          if (numericEditableFields.includes(params.field)) {
            const value = params.value;
            if (value === '' || value === null || value === undefined) return '';
            const num = Number(value);
            if (isNaN(num)) return '';
            // Don't format YearBuilt as a numeric string
            if (params.field === 'YearBuilt') return num;
            return num.toLocaleString();
          }
          if (booleanLikeFields.includes(params.field)) {
            // For InGroundPool, show blank for unknown values (null, undefined, empty string)
            if (params.field === 'InGroundPool') {
              if (params.value === null || params.value === undefined || params.value === '') {
                return '';
              }
              return params.value === 1 ? 'Yes' : 'No';
            }
            // For other boolean fields, keep existing logic
            return params.value === 1 ? 'Yes' : 'No';
          }
          if (params.field === 'parcel_id') {
            return (
              <CopyableIdCell 
                value={params.value} 
                label="Value"
                size="small"
              />
            );
          }
          return params.value;
        },
        // valueFormatter is now handled in columnDefinitions.js based on field type
        // Add validation for numeric fields

        // Non-numeric fields—just handle Tab
        ...(colType !== 'number' && {
          onKeyDown: (params) => {
            if (params.key === 'Tab') {
              params.event.preventDefault();
              handleTabNavigation(params, params.event);
            }
          }
        }),
        // Add validation for numeric fields
        ...(colType === 'number' && {
          onKeyDown: (params) => {
            const { key } = params;
            // Allow: backspace, delete, tab, escape, enter, decimal point, numbers
            const allowedKeys = [
              'Backspace', 'Delete', 'Tab', 'Escape', 'Enter', '.',
              '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
              'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'
            ];
            if (!allowedKeys.includes(key)) {
              params.event.preventDefault();
            }
          }
        })
      };
    }),
    rows: memoizedRows,
    pageSizeOptions: memoizedPageSizeOptions,
    localeText: {
      ...(localeText || {}),
      noRowsLabel: analysisHasRun ? noMissingDataLabel : 'Run Analysis to see results',
      noResultsOverlayLabel: analysisHasRun ? noMissingDataLabel : 'Run Analysis to see results',
      footerTotalRows: `Total Bad Data Rows:`
    },
    disableColumnMenu: true,
    disableColumnFilter: false,
    disableIgnoreModificationsIfProcessingProps: true,
    density: "compact",
    filterModel: hasDataColumns ? filterModel : EMPTY_FILTER_MODEL,
    onFilterModelChange: handleFilterModelChange,
    columnVisibilityModel,
    onColumnVisibilityModelChange: handleColumnVisibilityModelChange,
    slots: withSelectAllAwareCheckbox({
      toolbar: ToolbarSlot,
    }),
    showToolbar: true,
    disableRowSelectionExcludeModel: true,
    loading: isGridLoading,
    editMode: "cell",
    checkboxSelection: !(isGridLoading && !hasDataColumns),
    disableRowSelectionOnClick: true,
    rowSelectionModel: selectionModel,
    onRowSelectionModelChange: setSelectionModel,
    onCellDoubleClick: handleCellDoubleClick,
    processRowUpdate: (newRow, oldRow) => {
      const changedField = Object.keys(newRow).find(k => newRow[k] !== oldRow[k]);
      if (!changedField) return oldRow;

      // Store the original value if not already stored
      if (!unsavedChangesRef.current.rowsBeforeChange[oldRow.parcel_id]) {
        unsavedChangesRef.current.rowsBeforeChange[oldRow.parcel_id] = { ...oldRow };
      }

      // Update the unsaved changes
      if (!unsavedChangesRef.current.unsavedRows[oldRow.parcel_id]) {
        unsavedChangesRef.current.unsavedRows[oldRow.parcel_id] = {};
      }
      unsavedChangesRef.current.unsavedRows[oldRow.parcel_id][changedField] = newRow[changedField];

      // Force a re-render of the row
      apiRef.current.updateRows([{ id: newRow.id, [changedField]: newRow[changedField] }]);

      // Ensure update button gets enabled
      setHasEdits(true);

      return newRow;
    },
    onProcessRowUpdateError: (error) => {
      logger.error('Row update failed:', error);
    },
    getCellClassName: getCellClassName, // Pass the external getCellClassName function
    sx: {
      '& .missing-data-cell': {
        boxShadow: 'inset 0 0 0 1px red',
        boxSizing: 'border-box',
        backgroundColor: 'rgba(255, 255, 200, 0.3) !important',
      },
      '& .fixed-data-cell': {
        boxShadow: 'inset 0 0 0 2px #4caf50',
        color: '#2e7d32 !important',
        backgroundColor: 'rgba(76, 175, 80, 0.1) !important',
        fontWeight: 500,
        '&:hover': {
          backgroundColor: 'rgba(76, 175, 80, 0.15) !important',
        },
        '& .MuiCircularProgress-root': {
          margin: '0 auto'
        }
      },
      '& .uploading-photo-cell': {
        boxShadow: 'inset 0 0 0 2px #ffd700',
        color: '#ffd700 !important',
        backgroundColor: 'rgba(255, 215, 0, 0.1) !important',
        fontWeight: 500,
        '&:hover': {
          backgroundColor: 'rgba(255, 215, 0, 0.15) !important',
        },
        '& .MuiCircularProgress-root': {
          margin: '0 auto'
        }
      },
      '& .MuiDataGrid-cell.photo-cell-no-padding': {
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
        paddingLeft: '0 !important',
        paddingRight: '0 !important',
        margin: '0 !important',
      },
      '& .MuiDataGrid-cell.photo-cell-no-padding .MuiBox-root': {
        padding: '0 !important',
        margin: '0 !important',
      },
      '& .MuiDataGrid-cell.photo-cell-no-padding > *': {
        padding: '0 !important',
        margin: '0 !important',
      },
      '& .MuiDataGrid-cell:focus': {
        outline: 'none !important',
      },
      '& .MuiDataGrid-cell:focus-within': {
        outline: 'none !important',
      },
      '& .MuiDataGrid-cell--editing': {
        backgroundColor: 'transparent !important',
      },
      '& .upload-photo-label:hover': {
        backgroundColor: 'rgba(211, 47, 47, 0.04)',
        outline: '1px solid #1976d2'
      },
      '& .upload-photo-label:focus': {
        outline: '1px solid #1976d2'
      },
      '& .upload-photo-label:active': {
        backgroundColor: 'rgba(211, 47, 47, 0.08)'
      },
    },
    isCellEditable: isCellEditable, // Use the memoized function
    onCellClick: (params) => {
      // Don't interfere with PhotoURL cells - they have their own click handling
      if (params.field === 'PhotoURL') return;
      if (isReturnAllBadData) return;

      const description = params.row.Description || '';
      const fieldName = params.field;

      const hasIssue = description.includes(`(${fieldName}_NA)`) || 
                      description.includes(`(${fieldName}_EXTREME)`);

      // If there's no existing value, allow normal single-click edit when editable
      if (!hasExistingCellValue(params.value) && isCellEditable(params)) {
        startCellEdit(params);
        return;
      }

      if (hasIssue) {
        startCellEdit(params);
      }
    },
    // Add explicit filtering configuration
    getFilteredRowModel: (params) => {
      const items = params.filterModel?.items || [];
      const filteredRows = params.allRows.filter(row => {
        return items.every(filter => {
          if (filter.field === 'Description') return (row.Description || '').includes(filter.value);
          if (filter.field === 'parcel_id') return (row.parcel_id || '').startsWith(filter.value);
          if (filter.field === 'Client' || filter.field === 'IsSalesComp' || filter.field === 'IsAssessmentComp') {
            return row[filter.field] == filter.value;
          }
          return true;
        });
      });
      return filteredRows;
    },
    };
  }, [
    gridDisplayColumns,
    rows,
    memoizedPageSizeOptions,
    localeText,
    filterModel,
    handleFilterModelChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    isGridLoading,
    hasDataColumns,
    ToolbarSlot,
    isFlaggingData,
    selectionModel,
    handleCellEditCommit,
    getCellClassName,
    startCellEdit,
    handleCellDoubleClick,
    renderPhotoCell,
    isCellEditable,
    hasExistingCellValue,
    handleTabNavigation,
    hasFieldIssue,
    isReturnAllBadData,
    analysisHasRun,
    noMissingDataLabel,
  ]);

  const flagBadDataAndUpdateReport = useCallback(async () => {
    logger.log('🔍 [FLAG_BAD_DATA] Starting flagBadDataAndUpdateReport function');
    
    // Declare userUpdatedParcels outside try block so it's accessible in catch
    let userUpdatedParcels = [];
    
    try {
      const simpleEditedRows = unsavedChangesRef.current.unsavedRows;
      logger.log('🔍 [FLAG_BAD_DATA] Simple edited rows:', simpleEditedRows);
      // Array of the parcels the user updated.
      userUpdatedParcels = Object.keys(simpleEditedRows);
      logger.log('🔍 [FLAG_BAD_DATA] User updated parcels:', {
        count: userUpdatedParcels.length,
        parcels: userUpdatedParcels
      });
      
      // Only run database update if there are actual changes
      if (Object.keys(simpleEditedRows).length > 0) {
        logger.log('🔍 [FLAG_BAD_DATA] Updating database with user changes');
        const updateObj = await updateDatabaseNewFunction(simpleEditedRows, user?.userId);
        logger.log('✅ [FLAG_BAD_DATA] Database update completed');
      } else {
        logger.log('🔍 [FLAG_BAD_DATA] No user changes to update in database');
      }
      
      setIsFetching(true);
      let params = {
        MuniCode: negotiationObj?.MuniCode || 'All',
        TaxYear: negotiationObj?.TaxYear,
        county: negotiationObj?.county,
        municipality: negotiationObj?.municipality,
        village: negotiationObj?.village,
        villageRun: negotiationObj?.villageRun || 0,
        UserId: user?.userId || null,
        TrainingUser: user?.userAccess?.level === 'Training' ? 1 : 0,
        reportType: negotiationObj?.reportType || 'data_analysis',
        BadDataFields: localBadDataFlags.data,
        OutlierFields: outlierFieldsForBadDataRequest(localBadDataFlags),
        CourtDate: negotiationObj?.CourtDate || null,
        CourtRun: negotiationObj?.CourtRun || 0,
        runType: negotiationObj?.runType,
        uids: negotiationObj?.uids,
        overrideIds: negotiationObj?.overrideIds,
      };

      logger.log('🔍 [FLAG_BAD_DATA] Fetching bad data report with params:', {
        ...params,
        BadDataFields: localBadDataFlags.data,
        OutlierFields: outlierFieldsForBadDataRequest(localBadDataFlags)
      });
      
      const response = await fetchBadDataReport(params, {
        initialInvocation: false,
        setDataAnalysisResult,
        setDataAnalysisColumns,
        setDataAnalysisFilterModel,
        filterModel
      });

      logger.log('🔍 [FLAG_BAD_DATA] Bad data report response received:', {
        totalRecords: response.records?.length || 0,
        totalRows: response.total_rows,
        clientCount: response.client_count
      });

      logger.log('userUpdatedParcels', userUpdatedParcels);

      // HERE ADD A BLOCK TO UPDATE THE TASKS TABLE (with user ID) for the things that are EDITED
      // don't pass in the param to update these not "all but these"
      // have this go through and ADD IN THE ATTRIBUTION for the user id (assignee) who completed the task, based on the 
      const responseParcels = response.records.map(record => record.parcel_id);
      logger.log('🔍 [FLAG_BAD_DATA] Response parcels:', {
        count: responseParcels.length,
        sample: responseParcels.slice(0, 5)
      });
      
      const userUpdatedParcelsNotInResponse = userUpdatedParcels.filter(parcel => !responseParcels.includes(parcel));
      logger.log('🔍 [FLAG_BAD_DATA] User updated parcels not in response:', {
        count: userUpdatedParcelsNotInResponse.length,
        parcels: userUpdatedParcelsNotInResponse
      });
      
      if (userUpdatedParcelsNotInResponse.length > 0) {
        // This code updates tasks for parcels that were fixed by the user
        logger.log('🔍 [FLAG_BAD_DATA] User updated parcels that are no longer in response - updating tasks table');
        logger.log('🔍 [FLAG_BAD_DATA] Calling completeCaseStatus for user-fixed parcels:', {
          parcelIds: userUpdatedParcelsNotInResponse,
          reviewType: 'DataCleaning',
          muniCode: negotiationObj?.MuniCode || 'All',
          taxYear: negotiationObj?.TaxYear,
          userId: user?.userId
        });
        
        try {
          const updateResult = await completeCaseStatus(userUpdatedParcelsNotInResponse, 'DataCleaning', {
            muniCode: negotiationObj?.MuniCode || 'All',
            taxYear: negotiationObj?.TaxYear,
            userId: user?.userId,
            completeAllCaseTypes: true,
          });
          if (!response.dataCleaningAutoCompleteRan && (updateResult?.updated_count || 0) > 0) {
            toast.success('Data Cleaning Tasks have been marked completed');
          }
          logger.log('✅ [FLAG_BAD_DATA] Successfully updated tasks for user-fixed parcels');
        } catch (taskUpdateErr) {
          logger.error('❌ [FLAG_BAD_DATA] Error updating tasks and creating CaseReview tasks for fixed parcels:', taskUpdateErr);
        }
      } else {
        logger.log('🔍 [FLAG_BAD_DATA] No user-fixed parcels to update in tasks table');
      }


      logger.log('🔍 [FLAG_BAD_DATA] Setting response data in state:', {
        totalRows: response.total_rows,
        clientCount: response.client_count
      });
      
      setTotalRows(response.total_rows);
      setClientCount(response.client_count);
      
      // Reset edits state after successful update
      logger.log('🔍 [FLAG_BAD_DATA] Resetting edit state after successful update');
      setHasEdits(false);
      unsavedChangesRef.current = {
        unsavedRows: {},
        rowsBeforeChange: {}
      };

      logger.log('✅ [FLAG_BAD_DATA] Successfully completed flagBadDataAndUpdateReport');
      
    } catch (e) {
      logger.error('❌ [FLAG_BAD_DATA] Failed to flag bad data:', {
        error: e.message,
        stack: e.stack,
        userUpdatedParcels: userUpdatedParcels?.length || 0,
        negotiationObj: {
          MuniCode: negotiationObj?.MuniCode,
          TaxYear: negotiationObj?.TaxYear,
          reportType: negotiationObj?.reportType
        },
        user: {
          userId: user?.userId,
          userAccessLevel: user?.userAccess?.level
        }
      });
      if (e instanceof InventoryUpdateValidationError) {
        toast.error(e.message, INVENTORY_UPDATE_VALIDATION_TOAST_OPTIONS);
        const rowsToRevert = Object.values(unsavedChangesRef.current.rowsBeforeChange).map((row) => ({
          ...row,
          id: row.id || row.parcel_id,
        }));
        if (apiRef.current && rowsToRevert.length) {
          apiRef.current.updateRows(rowsToRevert);
        }
        unsavedChangesRef.current = { unsavedRows: {}, rowsBeforeChange: {} };
        setHasEdits(false);
        return;
      }
      logger.error('Failed to update data:', e);
    } finally {
      logger.log('🔍 [FLAG_BAD_DATA] Setting isFetching to false');
      setIsFetching(false);
    }
  }, [negotiationObj, setIsFetching, setDataAnalysisResult, setDataAnalysisColumns, setDataAnalysisFilterModel, localBadDataFlags, filterModel]);

  const dataSelectedCount = badDataFlags.data?.length || 0;

  const handleClearChanges = useCallback(() => {
    // Reset the unsaved changes ref
    unsavedChangesRef.current = {
      unsavedRows: {},
      rowsBeforeChange: {}
    };
    // Reset hasEdits state
    setHasEdits(false);
    // Force a re-render of the grid with proper IDs
    const rowsWithIds = rows.map(row => ({
      ...row,
      id: row.id || row.parcel_id
    }));
    apiRef.current.updateRows(rowsWithIds);
  }, [rows]);

  return (
    <div className="flex flex-col w-full">
      <div
        className="w-full gap-4"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          alignItems: 'end',
          width: '100%',
          gap: 16
        }}
      >
        <div style={{ alignSelf: 'end' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{
              minHeight: 36,
              height: 36,
              '& .MuiTabs-scroller': { height: '100%' },
              '& .MuiTab-root': {
                minHeight: 36,
                height: 36,
                padding: '4px 16px'
              }
            }}
          >
            <Tab label="Clients" value="clients" />
            <Tab label="Sales" value="salesComps" />
            <Tab label="Equity" value="equityComps" />
            <Tab label="All" value="all" />
          </Tabs>
        </div>
        <div style={{ justifySelf: 'center', alignSelf: 'stretch', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, paddingBottom: 8 }}>
          <SummaryStats {...summaryStatsProps} />
          <BadDataFlagsSelector
            uniqueFields={uniqueFields}
            dataSelectedCount={selectedFlags.length}
            fieldDisplayName={fieldDisplayName}
            externalBadDataFlags={localBadDataFlags}
            onBadDataFlagsChange={setLocalBadDataFlags}
            externalFlagType={localFlagType}
            onFlagTypeChange={setLocalFlagType}
            syncWithStore={false}
            isResetting={isResettingFlags}
          />
        </div>
        <div style={{ justifySelf: 'end', alignSelf: 'stretch', display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 8 }}>
          {selectedRowIds.length > 0 ? (
            <>
              <Typography variant="body2" color="textSecondary">
                {selectedRowIds.length} row{selectedRowIds.length > 1 ? 's' : ''} selected
              </Typography>
              <button
                disabled={isFlaggingData}
                onClick={handleBulkMenuClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#1976d2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  padding: '6px 16px',
                  fontWeight: 500,
                  fontSize: 14,
                  cursor: isFlaggingData ? 'not-allowed' : 'pointer',
                  opacity: isFlaggingData ? 0.6 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                Bulk Actions
                <ArrowDropDownIcon fontSize="small" />
              </button>
            </>
          ) : (
            <>
              <button
                disabled={!hasEdits || isUpdating || isFlaggingData}
                onClick={handleClearChanges}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#d32f2f',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  padding: '6px 16px',
                  fontWeight: 500,
                  fontSize: 14,
                  cursor: (!hasEdits || isUpdating || isFlaggingData) ? 'not-allowed' : 'pointer',
                  opacity: (!hasEdits || isUpdating || isFlaggingData) ? 0.6 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                Clear Changes
              </button>
              <button
                disabled={!updateEnabled || isUpdating || isFlaggingData}
                onClick={handleUpdateData}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#1976d2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  padding: '6px 16px',
                  fontWeight: 500,
                  fontSize: 14,
                  cursor: (!updateEnabled || isUpdating || isFlaggingData) ? 'not-allowed' : 'pointer',
                  opacity: (!updateEnabled || isUpdating || isFlaggingData) ? 0.6 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                <RefreshIcon fontSize="small" style={{ marginRight: 4 }} />
                Update Data
              </button>
            </>
          )}
        </div>
      </div>

      <div className={`w-full ${height ? `h-[${height}px]` : 'h-[calc(100vh-20vh-8px)]'}`}>
        <MemoizedDataGrid 
          apiRef={apiRef}
          dataGridProps={dataGridProps}
          isCellEditable={isCellEditable}
          handleTabNavigation={handleTabNavigation}
        />
      </div>
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'flex-end', 
        padding: '8px 16px',
        borderTop: '1px solid rgba(224, 224, 224, 1)',
        gap: '4px'
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Total Client Count: 
          {isUpdating || isFlaggingData || isEnrichingClientCases ? (
            <CircularProgress size={12} />
          ) : (
            clientCount.toLocaleString()
          )}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Total Record Count: 
          {isUpdating || isFlaggingData || isEnrichingClientCases ? (
            <CircularProgress size={12} />
          ) : (
            totalRows.toLocaleString()
          )}
        </Typography>
      </div>


      {/* Confirmation dialog for editing existing data */}
      <StableMuiDialog open={confirmEditOpen} onClose={cancelEdit} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Edit</DialogTitle>
        <DialogContent>
          You are editing data that already exists in the database. Please confirm this is intentional.
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelEdit} color="inherit">Cancel</Button>
          <Button onClick={confirmEdit} variant="contained" color="primary">Confirm</Button>
        </DialogActions>
      </StableMuiDialog>



      {/* Property info request confirmation dialog */}
      <StableMuiDialog open={propertyInfoConfirmOpen} onClose={handlePropertyInfoCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Property Info Request</DialogTitle>
        <DialogContent>
          {loadingTaskDetails ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
              <CircularProgress size={24} style={{ marginRight: 12 }} />
              <Typography>Loading task details...</Typography>
            </div>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>
                This property information request has already been completed. Do you want to request it again?
              </Typography>
              
              {pendingPropertyInfoRequest?.taskData && (
                <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Existing Task Details:
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Completed:</strong> {pendingPropertyInfoRequest.taskData.DateCompleted ? 
                      new Date(pendingPropertyInfoRequest.taskData.DateCompleted).toLocaleDateString() : 'Unknown'}
                  </Typography>
                  {pendingPropertyInfoRequest.taskData.Assignee && (
                    <Typography variant="body2" color="textSecondary">
                      <strong>Assigned to:</strong> {pendingPropertyInfoRequest.taskData.Assignee}
                    </Typography>
                  )}
                  {pendingPropertyInfoRequest.taskData.DateAssigned && (
                    <Typography variant="body2" color="textSecondary">
                      <strong>Assigned on:</strong> {new Date(pendingPropertyInfoRequest.taskData.DateAssigned).toLocaleDateString()}
                    </Typography>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePropertyInfoCancel} color="inherit">Cancel</Button>
          <Button onClick={handlePropertyInfoConfirm} variant="contained" color="primary">Request Again</Button>
        </DialogActions>
      </StableMuiDialog>

      {/* Bulk operations dropdown menu */}
      <Menu
        anchorEl={bulkMenuAnchorEl}
        open={bulkMenuOpen}
        onClose={handleBulkMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleBulkDataNotAvailable} disabled={isFlaggingData}>
          <CloseIcon sx={{ fontSize: 16, mr: 1 }} />
          Data not available ({selectedRowIds.length} rows)
        </MenuItem>
        <MenuItem onClick={handleBulkFlagBadSale} disabled={isFlaggingData || isFlaggingBadSale}>
          <FlagIcon sx={{ fontSize: 16, mr: 1 }} />
          Flag bad sale ({selectedRowIds.length} rows)
        </MenuItem>
        <MenuItem onClick={handleBulkRequestInventory} disabled={true}>
          <InfoIcon sx={{ fontSize: 16, mr: 1 }} />
          Request inventory ({selectedRowIds.length} rows)
        </MenuItem>
      </Menu>

      <FlagBadSaleDialog
        open={flagBadSaleDialogOpen}
        onClose={handleFlagBadSaleCancel}
        onConfirm={handleFlagBadSaleConfirm}
        submitting={isFlaggingBadSale}
        subtitle={`${selectedRowIds.length} selected sale${selectedRowIds.length === 1 ? '' : 's'}`}
        sales={memoizedRows
          .filter((row) => selectedRowIds.includes(row.id))
          .map((row) => ({ address: row.Address, price: row.SalePrice, date: row.SaleDate }))}
        confirmLabel={selectedRowIds.length > 1 ? `Flag ${selectedRowIds.length} sales` : 'Flag sale'}
      />

    </div>
  );
};

// Memoize the entire component to prevent unnecessary renders
export default memo(DataAnalysisDatagrid);